/**
 * BillingConsole — staff billing/onboarding tools, via staff-api. Nine operations,
 * listed in the order they RENDER: Stripe-writing tools first, Mongo-only ones last.
 *
 *   1. Apply Stripe Coupon        — attach (or remove) the customer's
 *                                   one Stripe discount                (Stripe + D1)
 *   2. PO / Invoice - Onboarding  — create the subscription, then onboard
 *      Process                      the account                    (Stripe + Mongo)
 *                                   (code + GraphQL still say "createStripeSubscription")
 *   3. PO / Invoice - Change      — raise/lower screens + add-ons on an
 *      Screens / Add-ons            EXISTING subscription        (Stripe + Mongo)
 *   4. Create Hardware Only Invoice    — draft a hardware invoice           (Stripe)
 *   5. Set Invoice Status         — mark a Stripe invoice paid/void     (Stripe)
 *   6. Normal Onboarding Process  — full paid-account setup            (Mongo)
 *   7. Userful Onboarding Process — Emerald/Userful enterprise setup   (Mongo)
 *   8. Update Screen Quantity     — adjust subscription screen count   (Mongo)
 *   9. Teams Billing – Adjusting  — same, for team-based billing        (Mongo)
 *      the Number of Screens
 *
 * A Quote Generator card leads the list. It is not an operation — it is a <Link> to the
 * quote route, and it performs no billing write of any kind.
 *
 * Ops 6-9 write Mongo directly (no webhooks) and carry the "Only Mongo Updated - " prefix
 * in the UI, which is what the (Mongo) column above means: they do not touch Stripe. Ops 1,
 * 4 and 5 touch Stripe ONLY, via staff-api's restricted key, and deliberately write no
 * Mongo billing state.
 *
 * Ops 2 and 3 do both. Op 2 is the Stripe half op 6 leaves to a human, followed by op 6's
 * own Mongo write — so the four Stripe ids op 6 asks an operator to paste are created and
 * read back rather than typed. Op 3 is the tool op 2's own guard defers to: op 2 refuses an
 * account that ALREADY has a subscription, op 3 refuses one that does not.
 *
 * Op 1 also READS the Document Center (D1): a coupon the catalog flags is refused
 * server-side unless a human-VERIFIED, non-archived tax-exempt certificate exists on
 * the account staff-api resolved from the Stripe customer.
 *
 * Every card is collapsed to its identifying field (a `cus_…`, or an invoice reference for
 * Set Invoice Status) until one is entered — the fields below it are all about a specific
 * customer, and several are seeded from a live read of them.
 *
 * The operator is the authenticated session (recorded in the audit row), so no
 * "submitted by" field is needed on any form.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { FileText } from 'lucide-react';
import { DocumentAttachment } from './DocumentAttachment';
import { DunningExclusionControl } from './DunningExclusionControl';
import {
  DOC_TYPE_LABEL,
  documentsApi,
  type CustomerDocument,
  type DocType,
} from '@/features/customers/lib/documents-api';
import { formatDateMedium, formatDayCentral } from '@/lib/format-date';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@staff-app/ui';
import {
  useBillingUpdateCompanyScreens,
  useBillingUpdateTeamScreens,
  useBillingNormalOnboarding,
  useBillingUserfulOnboarding,
  useBillingSetInvoiceStatus,
  useBillingHardwareCatalog,
  useBillingCreateHardwareInvoice,
  useBillingPlanCatalog,
  useBillingCreateStripeSubscription,
  useBillingSubscriptionSnapshot,
  useBillingChangeSubscription,
  useBillingStripeCustomer,
  useBillingCouponCatalog,
  useBillingCouponPreview,
  useBillingApplyCoupon,
  useBillingRemoveCoupon,
  STATE_REQUIRED_COUNTRIES,
  INVOICE_COMMENT_MAX,
  HARDWARE_MAX_LINES,
  type InvoiceStatusAction,
  type HardwareRegion,
  type HardwareCatalogProduct,
  type PlanCatalogAddOn,
  type StripeSubscriptionResult,
  type SubscriptionSnapshot,
  type SubscriptionChangeResult,
  type HardwareInvoiceResult,
  type StripeCustomerBilling,
  type CouponOption,
  type BillingCouponPreview,
  type BillingCustomerDiscount,
  type BillingTaxExemptDocument,
  type BillingCouponResult,
} from '../hooks/useBillingConsole';
import type { NormalOnboardingAddOn } from '../hooks/useBillingConsole';
import { useDeviceLines, optionKeyOf } from '../hooks/useDeviceLines';
import { DeviceLineRows } from './DeviceLineRows';
import {
  BILLING_COUNTRIES,
  BILLING_COUNTRY_NAME,
  SUBDIVISIONS_BY_COUNTRY,
} from '../billing-countries';

type ScreenQtyValues = { stripeId: string; total: string; previous: string; invoiceId: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TODAY = new Date().toISOString().slice(0, 10);

const NORMAL_PLAN_FEATURES = [
  { label: 'Standard', value: 'standard' },
  { label: 'Pro Plus', value: 'proPlus' },
  { label: 'Engage', value: 'engage' },
];
const CURRENCIES = [
  { label: 'USD', value: 'usd' },
  { label: 'EUR', value: 'eur' },
  { label: 'GBP', value: 'gbp' },
  { label: 'CAD', value: 'cad' },
  { label: 'AUD', value: 'aud' },
];
const USERFUL_PLAN_FEATURES = [
  { label: 'Emerald Standard', value: 'Emerald Standard' },
  { label: 'Emerald Plus', value: 'Emerald Plus' },
];
// Paid first so it is the default selection (see SetInvoiceStatusForm).
const INVOICE_STATUS_ACTIONS = [
  { label: 'Paid', value: 'paid' },
  { label: 'Void', value: 'void' },
];

/**
 * Stripe returns amounts in MINOR units, and the exponent is currency-specific
 * (USD 2, JPY 0). Ask Intl for the currency's fraction digits rather than
 * assuming /100, which would render ¥5000 as ¥50.
 */
/**
 * Render an auto-send deadline for the operator.
 *
 * In the OPERATOR'S OWN timezone, not the server's Central: this is a "how long have I
 * got" number, and a CS agent in another zone doing mental arithmetic on a CST string is
 * exactly how an invoice goes out before anyone reviews it. The relative half ("in about
 * 1 hour") is what people actually act on; the absolute time is there to remove
 * ambiguity.
 */
function formatFinalizeDeadline(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'at an unknown time';
  const mins = Math.round((at.getTime() - Date.now()) / 60000);
  const when = at.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  if (mins <= 0) return `at ${when} (due now)`;
  if (mins < 90) return `in about ${mins} minutes, at ${when}`;
  return `in about ${Math.round(mins / 60)} hours, at ${when}`;
}

function formatAmount(minor: number, currency: string): string {
  const cur = (currency || 'usd').toUpperCase();
  try {
    const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: cur });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    return fmt.format(minor / 10 ** digits);
  } catch {
    return `${minor} ${cur}`;
  }
}

/**
 * The five add-ons onboarding grants. Keys mirror `productPlans.add_ons`, which
 * is what the customer's own Account Add-Ons panel is built from, in the order
 * the portal shows them. AI and OptiDev are deliberately absent — they exist on
 * accounts (bought self-serve) and staff-api still re-derives their flags, but
 * they are not part of a sales-led onboarding.
 *
 * `quantified: false` = the catalog marks it `type: "onetime"`, so it has no
 * screen count (flat price, no licences).
 */
const ADD_ON_CATALOG: {
  key: string;
  label: string;
  quantified: boolean;
  tiers?: { label: string; value: string }[];
  /** Plans that grant the entitlement this add-on needs (see service ONBOARDING_ADD_ONS). */
  requiresPlan?: string[];
  note?: string;
}[] = [
  { key: 'aericast', label: 'AeriCast', quantified: true },
  { key: 'videowall', label: 'Video Wall', quantified: true },
  {
    key: 'backgroundmusic',
    label: 'OptiSound Music',
    quantified: true,
    tiers: [
      { label: 'Standard', value: 'standard' },
      { label: 'Pro', value: 'pro' },
    ],
    note: 'Pro also needs an account + slots provisioned in the OptiSound service — this form cannot do that.',
  },
  { key: 'engage', label: 'Engage', quantified: true },
  {
    key: 'brandingcustomdomain',
    label: 'Custom Domain',
    quantified: false,
    requiresPlan: ['proPlus', 'engage'],
    note: 'Sold as Pro Plus-or-higher plus this add-on. The plan is what unlocks branding; this row only records that they pay for it. The customer adds their own CNAME + SSL in Branding settings.',
  },
];

type AddOnRowState = { on: boolean; screens: string; tier: string; itemId: string };
const emptyAddOnRow = (): AddOnRowState => ({ on: false, screens: '', tier: '', itemId: '' });
const emptyAddOnState = (): Record<string, AddOnRowState> =>
  Object.fromEntries(ADD_ON_CATALOG.map((a) => [a.key, emptyAddOnRow()]));

import {
  ActionButtons,
  CardShell,
  LabeledInput,
  LabeledSelect,
  ResultLine,
  type Msg,
} from './console-ui';

/**
 * Add-on grants for Normal Onboarding. The tick gates the row; a ticked row's
 * fields become required (see `addOnsValid`). Unticked rows send nothing, so the
 * customer's existing add-ons are left alone.
 */
function AddOnSection({
  state,
  planFeature,
  onChange,
}: {
  state: Record<string, AddOnRowState>;
  /** Selected plan — some add-ons require Pro Plus or higher. */
  planFeature: string;
  onChange: (key: string, patch: Partial<AddOnRowState>) => void;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-dashed p-3" data-testid="billing-addons">
      <div className="flex flex-col gap-0.5">
        <p className="text-xs font-medium text-foreground">{'Add-ons (optional)'}</p>
        <p className="text-[11px] text-muted-foreground">
          {
            'Ticking an add-on licenses it; the customer assigns it to screens themselves. Each add-on has its own line on the customer’s Stripe subscription — paste that line’s id (si_…).'
          }
        </p>
        <p className="text-[11px] text-muted-foreground">
          {
            'Without it the next quantity change creates a second line and the add-on is billed twice.'
          }
        </p>
      </div>
      {ADD_ON_CATALOG.map((a) => {
        const row = state[a.key];
        const planBlocked = !!a.requiresPlan && !!planFeature && !a.requiresPlan.includes(planFeature);
        return (
          <div key={a.key} className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <label className="flex w-40 shrink-0 cursor-pointer items-center gap-2 text-xs">
                <Checkbox
                  checked={row.on}
                  onCheckedChange={(v) => onChange(a.key, { on: v === true })}
                  className="cursor-pointer"
                  data-testid={`billing-addon-${a.key}-toggle`}
                />
                {a.label}
              </label>
              {row.on ? (
                <>
                  {a.quantified ? (
                    <Input
                      value={row.screens}
                      onChange={(e) =>
                        onChange(a.key, { screens: e.target.value.replace(/[^0-9]/g, '') })
                      }
                      placeholder="Screens"
                      className="w-24"
                      aria-label={`${a.label} screens`}
                      data-testid={`billing-addon-${a.key}-screens`}
                    />
                  ) : null}
                  {a.tiers ? (
                    <Select
                      value={row.tier}
                      onValueChange={(v) => onChange(a.key, { tier: v })}
                    >
                      <SelectTrigger
                        className="w-32 cursor-pointer"
                        aria-label={`${a.label} tier`}
                        data-testid={`billing-addon-${a.key}-tier`}
                      >
                        <SelectValue placeholder="Tier" />
                      </SelectTrigger>
                      <SelectContent>
                        {a.tiers.map((t) => (
                          <SelectItem key={t.value} value={t.value} className="cursor-pointer">
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : null}
                  <Input
                    value={row.itemId}
                    onChange={(e) => onChange(a.key, { itemId: e.target.value.trim() })}
                    placeholder="si_… (required)"
                    className="w-56"
                    aria-label={`${a.label} Stripe subscription item id`}
                    data-testid={`billing-addon-${a.key}-itemId`}
                  />
                </>
              ) : null}
            </div>
            {row.on && planBlocked ? (
              <p className="pl-6 text-[11px] text-destructive" data-testid={`billing-addon-${a.key}-planError`}>
                {`Requires the ${a.requiresPlan!.join(' or ')} plan — the selected plan does not grant it.`}
              </p>
            ) : null}
            {row.on && a.note ? (
              <p className="pl-6 text-[11px] text-muted-foreground">{a.note}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** Ticked rows must be complete: screens for quantified add-ons, tier where tiered. */
function addOnsValid(state: Record<string, AddOnRowState>, planFeature: string): boolean {
  return ADD_ON_CATALOG.every((a) => {
    const row = state[a.key];
    if (!row.on) return true;
    if (a.quantified && !(Number(row.screens) > 0)) return false;
    if (a.tiers && !row.tier) return false;
    // Mirror the service: an add-on whose entitlement comes from the plan can't
    // be granted on a plan that doesn't include it.
    if (a.requiresPlan && !a.requiresPlan.includes(planFeature)) return false;
    // Every sold add-on has its own subscription line, so its id is required —
    // a row saved without it double-bills at the next quantity change. The
    // prefix guard catches a `sub_` pasted where an `si_` belongs.
    if (!row.itemId.startsWith('si_')) return false;
    return true;
  });
}

/** Ticked rows → mutation payload. */
function addOnsPayload(state: Record<string, AddOnRowState>): NormalOnboardingAddOn[] {
  return ADD_ON_CATALOG.filter((a) => state[a.key].on).map((a) => {
    const row = state[a.key];
    return {
      key: a.key,
      ...(a.quantified ? { maxQuantity: Number(row.screens) } : {}),
      subscriptionItemId: row.itemId,
      ...(a.tiers ? { tier: row.tier } : {}),
    };
  });
}

// ───────────────────────── Op 1: Normal Onboarding ──────────────────────────
function NormalOnboardingForm({
  stripeId,
  customer,
}: {
  /** The console's shared customer — this card no longer owns an id field. */
  stripeId: string;
  customer: StripeCustomerBilling | null;
}) {
  const KEY = 'normal-onboarding';
  const empty = {
    subscriptionId: '',
    subQty: '',
    subscriptionNormalId: '',
    invoiceId: '',
    planFeature: '',
    currency: '',
  };
  const [f, setF] = useState({ ...empty });
  const [addOns, setAddOns] = useState(emptyAddOnState);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const setAddOn = (key: string, patch: Partial<AddOnRowState>) =>
    setAddOns((s) => ({ ...s, [key]: { ...s[key], ...patch } }));
  const [onboard] = useBillingNormalOnboarding();

  const valid =
    stripeId.startsWith('cus_') &&
    f.subscriptionId.trim().length > 0 &&
    f.subQty.trim().length > 0 &&
    f.subscriptionNormalId.trim().length > 0 &&
    f.invoiceId.trim().length > 0 &&
    !!f.planFeature &&
    !!f.currency &&
    addOnsValid(addOns, f.planFeature);

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const grants = addOnsPayload(addOns);
      const r = (
        await onboard({
          variables: {
            stripeId: stripeId,
            subscriptionId: f.subscriptionId,
            subQty: Number(f.subQty),
            subscriptionNormalId: f.subscriptionNormalId,
            invoiceId: f.invoiceId,
            planFeature: f.planFeature,
            currency: f.currency,
            ...(grants.length ? { addOns: grants } : {}),
          },
        })
      ).data!.staffBillingNormalOnboarding;
      setMsg({ kind: 'ok', text: r.companyName ? `${r.companyName} — ${r.message}` : r.message });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };
  const clear = () => {
    setF({ ...empty });
    setAddOns(emptyAddOnState());
    setMsg(null);
  };

  /**
   * Collapsed until a customer id is entered — same rule as every other card in this console.
   * Nothing below is derived from the customer, so the gate is "an id is present", not "a
   * lookup succeeded"; these cards make no lookup at all.
   */
  const ready = /^cus_\w{6,}$/i.test(stripeId.trim());

  return (
    <CardShell
      testId={`billing-card-${KEY}`}
      title="Only Mongo Updated - Normal Onboarding Process"
      description="Set company payment, plan, subscription IDs, and feature flags for a new paying customer."
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      </div>

      {ready ? (
        <>
          <LabeledInput id={`${KEY}-subscriptionId`} label="Subscription ID" value={f.subscriptionId} onChange={set('subscriptionId')} placeholder="sub_..." testId={`billing-${KEY}-subscriptionId`} />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <LabeledInput id={`${KEY}-subQty`} label="Subscription Quantity" value={f.subQty} onChange={set('subQty')} placeholder="10" numeric testId={`billing-${KEY}-subQty`} />
        <LabeledInput id={`${KEY}-subscriptionNormalId`} label="Subscription Normal ID" value={f.subscriptionNormalId} onChange={set('subscriptionNormalId')} placeholder="si_..." helper="Stripe subscription ITEM id for the screens line — open-api updates this item on every screen-count change." testId={`billing-${KEY}-subscriptionNormalId`} />
        <LabeledInput id={`${KEY}-invoiceId`} label="Invoice ID" value={f.invoiceId} onChange={set('invoiceId')} placeholder="in_..." testId={`billing-${KEY}-invoiceId`} />
        <LabeledSelect id={`${KEY}-planFeature`} label="Plan Feature" value={f.planFeature} onChange={set('planFeature')} placeholder="Select plan feature" options={NORMAL_PLAN_FEATURES} testId={`billing-${KEY}-planFeature`} />
        <LabeledSelect id={`${KEY}-currency`} label="Currency" value={f.currency} onChange={set('currency')} placeholder="Select currency" options={CURRENCIES} testId={`billing-${KEY}-currency`} />
      </div>
      <AddOnSection state={addOns} planFeature={f.planFeature} onChange={setAddOn} />
        </>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-awaiting-customer`}>
          {'Enter a customer Stripe ID above — the rest of the form appears once it is filled in.'}
        </p>
      )}
      <ActionButtons keyId={KEY} submitLabel="Submit Onboarding" valid={valid} busy={busy} onSubmit={submit} onClear={clear} />
      <ResultLine msg={msg} testId={`billing-${KEY}-result`} />
    </CardShell>
  );
}

// ───────────────────────── Op 2: Userful Onboarding ─────────────────────────
function UserfulOnboardingForm({
  stripeId,
  customer,
}: {
  /** The console's shared customer — this card no longer owns an id field. */
  stripeId: string;
  customer: StripeCustomerBilling | null;
}) {
  const KEY = 'userful-onboarding';
  const empty = {
    subscriptionId: '',
    subQty: '',
    subscriptionNormalId: '',
    planFeature: '',
    email: '',
    poNumber: '',
    contractPeriod: '',
    purchaseAmount: '',
    licenseEndDate: '',
    invoiceId: '',
    creditNoteId: '',
  };
  const [f, setF] = useState({ ...empty });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const [onboard] = useBillingUserfulOnboarding();

  const valid =
    stripeId.startsWith('cus_') &&
    [f.subscriptionId, f.subQty, f.subscriptionNormalId, f.poNumber, f.contractPeriod, f.purchaseAmount, f.licenseEndDate, f.invoiceId, f.creditNoteId].every(
      (v) => v.trim().length > 0,
    ) &&
    !!f.planFeature &&
    EMAIL_RE.test(f.email.trim());

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = (
        await onboard({
          variables: {
            stripeId: stripeId,
            subscriptionId: f.subscriptionId,
            subQty: Number(f.subQty),
            subscriptionNormalId: f.subscriptionNormalId,
            planFeature: f.planFeature,
            email: f.email,
            poNumber: f.poNumber,
            contractPeriod: f.contractPeriod,
            purchaseAmount: f.purchaseAmount,
            licenseEndDate: f.licenseEndDate,
            invoiceId: f.invoiceId,
            creditNoteId: f.creditNoteId,
          },
        })
      ).data!.staffBillingUserfulOnboarding;
      setMsg({ kind: 'ok', text: r.companyName ? `${r.companyName} — ${r.message}` : r.message });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };
  const clear = () => {
    setF({ ...empty });
    setMsg(null);
  };

  /**
   * Collapsed until a customer id is entered — same rule as every other card in this console.
   * Nothing below is derived from the customer, so the gate is "an id is present", not "a
   * lookup succeeded"; this card makes no lookup at all.
   */
  const ready = /^cus_\w{6,}$/i.test(stripeId.trim());

  return (
    <CardShell
      testId={`billing-card-${KEY}`}
      title="Only Mongo Updated - Userful Onboarding Process"
      description="Set company payment, limitation, and feature flags for Userful / Emerald enterprise plans."
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      </div>

      {ready ? (
        <>
          <LabeledInput id={`${KEY}-subscriptionId`} label="Subscription ID" value={f.subscriptionId} onChange={set('subscriptionId')} placeholder="sub_..." testId={`billing-${KEY}-subscriptionId`} />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <LabeledInput id={`${KEY}-subQty`} label="Subscription Quantity" value={f.subQty} onChange={set('subQty')} placeholder="10" numeric testId={`billing-${KEY}-subQty`} />
        <LabeledInput id={`${KEY}-subscriptionNormalId`} label="Subscription Normal ID" value={f.subscriptionNormalId} onChange={set('subscriptionNormalId')} placeholder="sub_..." testId={`billing-${KEY}-subscriptionNormalId`} />
        <LabeledSelect id={`${KEY}-planFeature`} label="Plan Feature" value={f.planFeature} onChange={set('planFeature')} placeholder="Select plan feature" options={USERFUL_PLAN_FEATURES} helper='Plan feature will be sent as "pro" to the backend.' testId={`billing-${KEY}-planFeature`} />
        <LabeledInput id={`${KEY}-email`} label="Email" type="email" value={f.email} onChange={set('email')} placeholder="customer@example.com" testId={`billing-${KEY}-email`} />
        <LabeledInput id={`${KEY}-poNumber`} label="PO Number" value={f.poNumber} onChange={set('poNumber')} placeholder="PO-12345" testId={`billing-${KEY}-poNumber`} />
        <LabeledInput id={`${KEY}-contractPeriod`} label="Contract Period (Year)" value={f.contractPeriod} onChange={set('contractPeriod')} placeholder="1" numeric testId={`billing-${KEY}-contractPeriod`} />
        <LabeledInput id={`${KEY}-purchaseAmount`} label="Purchase Order Amount" value={f.purchaseAmount} onChange={set('purchaseAmount')} placeholder="1000.00" testId={`billing-${KEY}-purchaseAmount`} />
        <LabeledInput id={`${KEY}-licenseEndDate`} label="License End Date" type="date" value={f.licenseEndDate} onChange={set('licenseEndDate')} min={TODAY} testId={`billing-${KEY}-licenseEndDate`} />
        <LabeledInput id={`${KEY}-invoiceId`} label="OptiSigns Invoice ID" value={f.invoiceId} onChange={set('invoiceId')} placeholder="INV-..." testId={`billing-${KEY}-invoiceId`} />
        <LabeledInput id={`${KEY}-creditNoteId`} label="OptiSigns Credit Note ID" value={f.creditNoteId} onChange={set('creditNoteId')} placeholder="CN-..." testId={`billing-${KEY}-creditNoteId`} />
      </div>
        </>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-awaiting-customer`}>
          {'Enter a customer Stripe ID above — the rest of the form appears once it is filled in.'}
        </p>
      )}
      <ActionButtons keyId={KEY} submitLabel="Submit Userful Onboarding" valid={valid} busy={busy} onSubmit={submit} onClear={clear} />
      <ResultLine msg={msg} testId={`billing-${KEY}-result`} />
    </CardShell>
  );
}

// ──────────────── Ops 3 & 4: Update (Company / Team) Screen Quantity ─────────
function ScreenQtyForm({
  keyId,
  title,
  description,
  onSubmit,
  sharedStripeId,
  ownId,
  customer,
}: {
  keyId: string;
  title: string;
  description: string;
  onSubmit: (v: ScreenQtyValues) => Promise<string>;
  /**
   * The console's shared customer, when this instance is scoped to it.
   *
   * Present for the COMPANY card and deliberately absent for the TEAM one, and the difference
   * is not cosmetic: `billingUpdateTeamScreens` resolves via
   * `_salesResolveCompany(stripeId, 'teams')` — a team's Stripe id is a different object from
   * its company's. Feeding the shared customer to the team card would resolve nothing and the
   * bar at the top would be lying about what that card acts on, so the team card keeps its own
   * field. This one prop is why the component is shared rather than duplicated.
   */
  sharedStripeId?: string;
  /**
   * True when this instance acts on its OWN id rather than the console's customer.
   *
   * An explicit flag, not `sharedStripeId === undefined`, because the team instance needs the
   * console's customer for the GATE while still acting on its own team id — deriving one from
   * the other conflated "which id do I act on" with "may I show anything at all".
   */
  ownId?: boolean;
  customer?: StripeCustomerBilling | null;
}) {
  const ownsId = !!ownId;
  const [f, setF] = useState({ stripeId: '', total: '', previous: '', invoiceId: '' });
  /** The id this instance acts on: the console's, or its own when it is not customer-scoped. */
  const stripeId = ownsId ? f.stripeId : (sharedStripeId ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const setNum = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v.replace(/[^0-9]/g, '') }));

  // Live warning: no change when new total equals the previous count.
  const sameValue = !!f.total && !!f.previous && f.total === f.previous;

  const valid =
    // `stripeId`, not `f.stripeId`: for the company instance the id comes from the console.
    stripeId.startsWith('cus_') &&
    f.total.trim().length > 0 &&
    f.previous.trim().length > 0 &&
    f.invoiceId.trim().length > 0;

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      // `stripeId` explicitly, NOT the raw `f`: for the company instance the id lives in the
      // console, so `f.stripeId` is empty and passing `f` alone submitted a blank customer.
      const text = await onSubmit({ ...f, stripeId });
      setMsg({ kind: 'ok', text });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };
  const clear = () => {
    // Keep a shared customer (not ours to clear); reset our own team id.
    setF({ stripeId: ownsId ? '' : f.stripeId, total: '', previous: '', invoiceId: '' });
    setMsg(null);
  };

  /**
   * Collapsed until a customer id is entered. The screen counts are the whole payload here,
   * and they are meaningless without the account they apply to.
   */
  /**
   * TWO gates, because this form serves two different ids.
   *
   * `customerPresent` is the console's customer. It reveals this card's own inputs at all —
   * including the TEAM instance's own id fields, so the whole console obeys one rule: pick a
   * customer, then work.
   *
   * `ready` is the id this instance actually acts on. For the team instance that is its own
   * `cus_…` (a team bills separately from its parent, so it is a different object), which is
   * why the screen counts wait for a second thing to be typed there and not on the company
   * instance.
   */
  const customerPresent = /^cus_\w{6,}$/i.test((sharedStripeId ?? '').trim());
  const ready = customerPresent && /^cus_\w{6,}$/i.test(stripeId.trim());

  return (
    <CardShell testId={`billing-card-${keyId}`} title={title} description={description}>
      {customerPresent ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {ownsId ? (
            <LabeledInput id={`${keyId}-stripeId`} label="Team Stripe ID" value={f.stripeId} onChange={set('stripeId')} placeholder="cus_..." helper="Enter the TEAM's Stripe Customer ID (e.g., cus_ABC123) — a team bills separately from its parent account, so this is not the customer above." testId={`billing-${keyId}-stripeId`} />
          ) : (
            <></>
          )}
          <LabeledInput id={`${keyId}-invoiceId`} label="Stripe Invoice ID" value={f.invoiceId} onChange={set('invoiceId')} placeholder="in_..." helper="Enter the Stripe Invoice ID (e.g., in_ABC123)" testId={`billing-${keyId}-invoiceId`} />
        </div>
      ) : null}

      {ready ? (
        <>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <LabeledInput id={`${keyId}-total`} label="Total Number of Screens (New)" value={f.total} onChange={setNum('total')} placeholder="10" helper="The new total number of screens after change" testId={`billing-${keyId}-total`} />
        <LabeledInput id={`${keyId}-previous`} label="Previous Number of Screens" value={f.previous} onChange={setNum('previous')} placeholder="5" helper="The previous number of screens before change" testId={`billing-${keyId}-previous`} />
      </div>
        </>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid={`billing-${keyId}-awaiting-customer`}>
          {!customerPresent
            ? 'Enter a customer Stripe ID above — this form appears once it is filled in.'
            : ownsId
              ? "Enter the TEAM's Stripe ID above — the screen counts appear once it is filled in."
              : 'Enter a customer Stripe ID above — the screen counts appear once it is filled in.'}
        </p>
      )}
      {sameValue ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${keyId}-warning`}
        >
          {'⚠️ New total equals the previous count — no change will occur.'}
        </p>
      ) : null}
      <ActionButtons keyId={keyId} submitLabel="Update Screen Quantity" valid={valid} busy={busy} onSubmit={submit} onClear={clear} />
      <ResultLine msg={msg} testId={`billing-${keyId}-result`} />
    </CardShell>
  );
}

// ───────────────────── Op 5: Set Invoice Status (Stripe) ─────────────────────
/**
 * The only Billing Console op that talks to Stripe (via staff-api's restricted
 * `Invoices: Write` key). PAID uses `paid_out_of_band` — it records settlement
 * and charges nothing. Stripe-only: it does NOT reactivate a suspended account.
 */
function SetInvoiceStatusForm() {
  const KEY = 'set-invoice-status';
  const empty = { invoiceRef: '', action: 'paid' as InvoiceStatusAction, comment: '' };
  const [f, setF] = useState({ ...empty });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [setStatus] = useBillingSetInvoiceStatus();

  const comment = f.comment.trim();
  const overLimit = comment.length > INVOICE_COMMENT_MAX;
  const valid = f.invoiceRef.trim().length > 0 && comment.length > 0 && !overLimit;

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = (
        await setStatus({
          variables: {
            invoiceRef: f.invoiceRef.trim(),
            action: f.action,
            comment,
          },
        })
      ).data!.staffBillingSetInvoiceStatus;
      // Echo the invoice identity + billing_reason: this tool accepts ANY
      // invoice, and a device-order invoice's Mongo status is NOT synced, so the
      // operator needs to see what kind of invoice they just changed.
      const bits = [
        r.number ? `${r.number} (${r.invoiceId})` : r.invoiceId,
        formatAmount(r.total, r.currency),
        r.billingReason ? `reason: ${r.billingReason}` : null,
      ].filter(Boolean);
      setMsg({ kind: 'ok', text: `${r.message} — ${bits.join(' · ')}` });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };
  const clear = () => {
    setF({ ...empty });
    setMsg(null);
  };

  /**
   * Collapsed until the invoice is named.
   *
   * This card has NO customer id — an invoice reference is its identifying field, so that is
   * what the gate keys on. Any `in_…` or invoice number counts; the card resolves it
   * server-side, and pre-validating the format here would refuse the invoice NUMBERS the op
   * deliberately accepts.
   */
  const ready = f.invoiceRef.trim().length > 0;

  return (
    <CardShell
      testId={`billing-card-${KEY}`}
      title="Set Invoice Status"
      description="Mark a Stripe invoice paid (recorded as paid outside Stripe — no card is charged) or void it. Changes Stripe only: it does NOT reactivate a suspended account — use the Sales Console for that."
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <LabeledInput
          id={`${KEY}-invoiceRef`}
          label="Invoice ID or Invoice Number"
          value={f.invoiceRef}
          onChange={(v) => setF((s) => ({ ...s, invoiceRef: v }))}
          placeholder="in_1ABC... or INV-0042"
          helper="Stripe invoice ID (in_...) or the invoice number"
          testId={`billing-${KEY}-invoiceRef`}
        />
        <LabeledSelect
          id={`${KEY}-action`}
          label="Status"
          value={f.action}
          onChange={(v) => setF((s) => ({ ...s, action: v as InvoiceStatusAction }))}
          placeholder="Select status"
          options={INVOICE_STATUS_ACTIONS}
          helper="Void is irreversible in Stripe."
          testId={`billing-${KEY}-action`}
        />
      </div>
      {ready ? (
        <>
        <LabeledInput
          id={`${KEY}-comment`}
          label="Comment (recorded on the invoice)"
          value={f.comment}
          onChange={(v) => setF((s) => ({ ...s, comment: v }))}
          placeholder="Paid by wire, ref 88213 / PO 4471"
          helper={`Required — why the status was changed by hand. ${comment.length}/${INVOICE_COMMENT_MAX}`}
          testId={`billing-${KEY}-comment`}
        />
        {overLimit ? (
          <p
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            data-testid={`billing-${KEY}-overlimit`}
          >
            {`Comment is ${comment.length} characters — Stripe allows at most ${INVOICE_COMMENT_MAX}.`}
          </p>
        ) : null}
        </>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-awaiting-customer`}>
          {'Enter the invoice above — the comment appears once it is filled in.'}
        </p>
      )}
      <ActionButtons
        keyId={KEY}
        submitLabel="Process"
        valid={valid}
        busy={busy}
        onSubmit={submit}
        onClear={clear}
      />
      <ResultLine msg={msg} testId={`billing-${KEY}-result`} />
    </CardShell>
  );
}

// ─────────────────────── Quote Generator (entry point only) ──────────────────────
/**
 * Unlike every other card here, this one performs no operation — it opens the Quote
 * Generator, which is a full page because the quote is a Letter-sized printable
 * document. Pricing comes from the live catalog (deviceProducts / deviceShippings /
 * productPlans) via the deterministic engine ported from OptiClaw quote-gen, so a rep's
 * quote matches an autonomously generated one to the cent.
 */
function QuoteGeneratorCard() {
  return (
    <CardShell
      testId="billing-card-quote-generator"
      title="Quote Generator"
      description="Build a customer quote from live OptiSigns pricing — subscriptions, hardware and shipping — then print it to PDF. Annual terms only; nothing is saved."
    >
      <div className="flex items-center gap-2">
        <Button asChild className="cursor-pointer" data-testid="billing-quote-generator-open">
          <Link to="/staff/optihub/quote-generator">
            <FileText className="mr-1 h-4 w-4" />
            {'Open Quote Generator'}
          </Link>
        </Button>
      </div>
    </CardShell>
  );
}

// ───────────────────── Stripe Quote Generator (entry point only) ─────────────────────
/**
 * Opens the Stripe Quote Generator — a native Stripe quote (Stripe numbers it, prices it, taxes
 * it and renders the PDF) rather than a printed page. Runs beside the Quote Generator above
 * while the two are compared.
 */
function StripeQuoteGeneratorCard() {
  return (
    <CardShell
      testId="billing-card-stripe-quote-generator"
      title="Stripe Quote Generator"
      description="Create a real quote in Stripe for a US customer — subscription, add-ons and hardware with shipping — and download Stripe's PDF. Yearly terms; discounts on subscription lines only. The quote is not accepted here."
    >
      <div className="flex items-center gap-2">
        <Button asChild className="cursor-pointer" data-testid="billing-stripe-quote-generator-open">
          <Link to="/staff/optihub/stripe-quote-generator">
            <FileText className="mr-1 h-4 w-4" />
            {'Open Stripe Quote Generator'}
          </Link>
        </Button>
      </div>
    </CardShell>
  );
}

/**
 * Record the invoice a document was attached alongside.
 *
 * Runs AFTER the card's action succeeds, because the file is uploaded the moment it is
 * chosen and the invoice does not exist until later. Best-effort on purpose: the invoice is
 * real and the document is stored either way, so a failure here must not turn a successful
 * create into an error — it only costs the cross-reference, which can be added by hand.
 */
/**
 * What became of the document→invoice cross-reference. Reported rather than swallowed: a
 * missing link used to be invisible, and the operator is the only one who can fix it.
 */
type DocLinkState =
  | { kind: 'none' }
  | { kind: 'linked'; invoiceId: string }
  | { kind: 'no-invoice' }
  | { kind: 'failed' };

/**
 * The one line that says whether the attached document ended up cross-referenced. Rendered by
 * every card that can attach one, so the answer reads the same wherever you are.
 */
function DocLinkLine({ state, keyId }: { state: DocLinkState; keyId: string }) {
  if (state.kind === 'none') return null;
  const testId = `billing-${keyId}-doc-link`;
  if (state.kind === 'linked') {
    return (
      <p className="text-xs text-muted-foreground" data-testid={testId}>
        {`Document filed against the customer and cross-referenced to ${state.invoiceId}.`}
      </p>
    );
  }
  if (state.kind === 'no-invoice') {
    return (
      <p className="text-xs text-warning" data-testid={testId}>
        {'Document filed against the customer, but this action raised no invoice — so it carries NO invoice cross-reference. Link it from Document Center once the invoice exists.'}
      </p>
    );
  }
  return (
    <p className="text-xs text-destructive" data-testid={testId}>
      {'Document filed against the customer, but writing the invoice cross-reference FAILED. The invoice and the document are both fine — add the link from Document Center.'}
    </p>
  );
}

async function linkInvoiceToDocument(
  documentId: string | null,
  invoiceId: string | null | undefined,
  invoiceUrl: string | null | undefined,
  /**
   * How many screens this deal was for — the onboarding card's Subscription Quantity, or the
   * change card's delta.
   *
   * Recorded HERE because here is the only place it is known. Nothing durable stores it: the
   * change op writes the resulting `limitation.maxQuantity` with a raw $set and no audit row,
   * so the INCREASE is not recoverable afterwards from Mongo, and a document row has an amount
   * but no quantity. Stamping it onto the document at the moment the card runs is what lets the
   * Document Center show "how big was this deal" at all.
   */
  screens?: number | null,
): Promise<DocLinkState> {
  if (!documentId) return { kind: 'none' };
  // No invoice to point at — the NEXT_INVOICE proration path with no hardware, or an immediate
  // change Stripe decided not to prorate. The SCREENS are still worth recording, so this writes
  // what it knows and then reports the missing cross-reference.
  if (!invoiceId) {
    if (typeof screens === 'number') {
      try {
        // Safe to send this alone: the server merge-patches `meta`, so writing the quantity
        // cannot drop a cross-reference some other path already put there.
        await documentsApi.patch(documentId, { meta: { screens } } as never);
      } catch {
        /* the cross-reference is the reportable part; a lost quantity is not worth a second
           failure mode on screen */
      }
    }
    return { kind: 'no-invoice' };
  }
  try {
    await documentsApi.patch(documentId, {
      meta: {
        invoiceId,
        invoiceUrl: invoiceUrl ?? null,
        ...(typeof screens === 'number' ? { screens } : {}),
      },
    } as never);
    return { kind: 'linked', invoiceId };
  } catch {
    // Still never thrown: the invoice is real and the document is stored either way. But it is
    // no longer SILENT — the caller reports it so the cross-reference can be added by hand
    // from Document Center instead of being discovered months later.
    return { kind: 'failed' };
  }
}

// ─────────────── Op 6: Create Hardware Only Invoice (Stripe) ────────────────
const SHIP_REGIONS: { label: string; value: HardwareRegion }[] = [
  { label: 'United States', value: 'us' },
  { label: 'International', value: 'international' },
];


/**
 * Creates a DRAFT Stripe invoice for hardware, priced from Mongo's
 * `deviceProducts` / `deviceShippings` and billed against the EXISTING Stripe
 * products. Stripe-only: it writes no device-order record, so nothing here
 * triggers fulfillment, shipping, or provisioning.
 *
 * The SKU / currency / shipping options are all served by the catalog query, so
 * this form never hardcodes a product list and can't disagree with the catalog.
 */
function CreateHardwareInvoiceForm({
  stripeId,
  customer,
}: {
  /** The console's shared customer — this card no longer owns an id field. */
  stripeId: string;
  customer: StripeCustomerBilling | null;
}) {
  const KEY = 'create-hardware-invoice';
  const empty = {
    currency: 'usd',
    region: 'us' as HardwareRegion,
    comment: '',
  };
  const [f, setF] = useState({ ...empty });
  const [busy, setBusy] = useState(false);
  /** The created draft, kept whole so the outcome can link to it. Null until one exists. */
  const [result, setResult] = useState<HardwareInvoiceResult | null>(null);
  // Set when a file is attached; used to back-fill the invoice id once the create
  // succeeds. Cleared with the form so a later invoice cannot adopt an old document.
  const [attachedDocId, setAttachedDocId] = useState<string | null>(null);
  /** Whether that document ended up cross-referenced — surfaced, not swallowed. */
  const [docLink, setDocLink] = useState<DocLinkState>({ kind: 'none' });
  const [msg, setMsg] = useState<Msg>(null);

  const [createInvoice] = useBillingCreateHardwareInvoice();

  /**
   * Every rule about WHICH products may be offered, what a currency or region change
   * invalidates, and what counts as a valid set of lines comes from the shared hook — the
   * same one the two subscription cards use.
   *
   * This card is where all of it was first written; it was lifted out when a second card
   * needed it, and this one kept its own copy for one release. That is the drift this
   * removes: three cards deciding independently what a product costs, whether a kiosk can
   * ship abroad, and what a currency change should silently drop.
   *
   * What is NOT shared is the presentation below. This card's notices are deliberately more
   * specific than the shared component's — they name `STRIPE_HARDWARE_PRODUCTS` and
   * `deviceProducts`, because the person reading them here is usually the one who has to go
   * and fix the mapping — and its testids are its own. Sharing the money logic is the point;
   * sharing the wording would have made both cards' messages worse.
   */
  const devices = useDeviceLines({ currency: f.currency, region: f.region });
  const {
    rows,
    setRows,
    options,
    allProducts,
    catalog,
    catalogLoading,
    catalogError,
    subtotal,
    filledRows,
    duplicateSku,
    badQuantity,
    pruned,
    productName,
  } = devices;

  /** The catalog returns Standard only; there is no method choice to make. */
  const shippingMethod = catalog?.shippingMethods?.[0];

  const comment = f.comment.trim();
  const overLimit = comment.length > INVOICE_COMMENT_MAX;

  const valid =
    /^cus_/i.test(stripeId.trim()) &&
    filledRows.length > 0 &&
    !duplicateSku &&
    !badQuantity &&
    comment.length > 0 &&
    !overLimit;

  /**
   * Currency and region live in THIS card's form state, so the prune and the state change are
   * two steps: the hook drops the lines the new value invalidates and names what it dropped,
   * and the caller moves the field. Kept that way because for the onboarding card the currency
   * is the SUBSCRIPTION's, not a field of its own.
   */
  const changeCurrency = (currency: string) => {
    devices.pruneForCurrency(currency);
    setF((s) => ({ ...s, currency }));
  };

  const changeRegion = (region: HardwareRegion) => {
    devices.pruneForRegion(region);
    setF((s) => ({ ...s, region }));
  };

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = (
        await createInvoice({
          variables: {
            input: {
              stripeId: stripeId.trim(),
              currency: f.currency,
              region: f.region,
              lines: devices.payloadLines(),
              comment,
              // The file is already filed against the customer by the time submit is live —
              // the attachment control uploads on pick. Sending its id is what puts the
              // reverse link (`optisigns_po_doc_url`) on the Stripe invoice; without it the
              // cross-reference exists only on the document side.
              ...(attachedDocId ? { documentId: attachedDocId } : {}),
            },
          },
        })
      ).data!.staffBillingCreateHardwareInvoice;

      // The outcome carries a dashboard deep link, so it renders as a clickable result
      // (like Create Stripe Subscription) rather than a URL pasted into a sentence.
      setResult(r);
      setMsg(null);
      setDocLink(await linkInvoiceToDocument(attachedDocId, r.invoiceId, r.dashboardUrl));
    } catch (e) {
      setResult(null);
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Everything from the shipping region down stays hidden until a customer id is entered.
   *
   * Same gate and same reasoning as the PO / Invoice card: nothing below is DERIVED from the
   * customer (the region drives the rate, the products come from the catalog), so the gate is
   * "an id has been entered" rather than "a lookup succeeded" — this card makes no customer
   * lookup at all. The document attachment is the one part that genuinely needs it: its own
   * copy already said "Enter the Customer Stripe ID above first: it decides which customer
   * the file is filed under", which is a hint that only existed because the field was
   * reachable too early.
   */
  const ready = /^cus_\w{6,}$/i.test(stripeId.trim());

  const clear = () => {
    setAttachedDocId(null);
    setDocLink({ kind: 'none' });
    setF({ ...empty });
    devices.reset();
    setMsg(null);
    // Clear the previous draft's link too — leaving it would invite a click that opens an
    // unrelated invoice while the operator is filling in the next one.
    setResult(null);
  };

  return (
    <CardShell
      testId={`billing-card-${KEY}`}
      title="Create Hardware Only Invoice"
      description="Creates a Stripe invoice for hardware, priced live from the device catalog and billed against the existing Stripe products. It is created as a DRAFT and then SENDS ITSELF — Stripe finalizes and emails it to the customer about an hour after you create it, so review it in Stripe before that deadline. The exact time is shown once it is created. No discount applies to a hardware invoice — every line is non-discountable, so change the catalog price or edit the draft line instead of adding a coupon. It does not ship or provision anything itself, but finalizing starts the unpaid-invoice chase, so do not raise one speculatively."
    >
      {catalogError ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid={`billing-${KEY}-catalog-error`}
        >
          {`Could not load the device catalog: ${catalogError.message}`}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      </div>

      {ready ? (
        <>
          <LabeledSelect
            id={`${KEY}-currency`}
            label="Currency"
            value={f.currency}
            onChange={changeCurrency}
            placeholder="Select currency"
            options={CURRENCIES}
            helper="Only SKUs priced in this currency can be added."
            testId={`billing-${KEY}-currency`}
          />
        {/* Region + shipping method get their own two-column grid: the gate has to wrap whole
            elements, and splitting the grid above across a conditional leaves an unbalanced
            tree. */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <LabeledSelect
          id={`${KEY}-region`}
          label="Shipping region"
          value={f.region}
          onChange={(v) => changeRegion(v as HardwareRegion)}
          placeholder="Select region"
          options={SHIP_REGIONS}
          helper={
            f.region === 'us'
              ? "Picks the US rate. Stripe calculates US tax at finalization from the customer's address on file."
              : 'Picks the international rate. No automatic tax outside the US, and kiosks are not offered — they do not ship internationally yet.'
          }
          testId={`billing-${KEY}-region`}
        />
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{'Shipping method'}</Label>
          {/* Not a picker: staff hardware invoices always ship Standard. Read from
              the catalog rather than hardcoded so a rename can't make this lie. */}
          <p
            className="rounded-md border bg-muted/40 px-3 py-2 text-sm"
            data-testid={`billing-${KEY}-shipping`}
          >
            {catalogLoading ? 'Loading…' : shippingMethod?.name ?? 'Standard Shipping & Handling'}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {shippingMethod && !shippingMethod.regions.includes(f.region)
              ? `No ${f.region === 'us' ? 'US' : 'international'} rate for this method in deviceShippings.`
              : 'Always Standard. Rates come from deviceShippings, billed as its own line.'}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label className="text-xs">Hardware</Label>
        {rows.map((row, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="flex-1">
              <LabeledSelect
                id={`${KEY}-sku-${i}`}
                label={i === 0 ? 'Product' : ''}
                value={row.optionKey}
                onChange={(v) =>
                  setRows((s) => s.map((r, j) => (j === i ? { ...r, optionKey: v } : r)))
                }
                placeholder={catalogLoading ? 'Loading…' : 'Select product'}
                options={options.map((p) => ({
                  label: `${p.name} — ${formatAmount(
                    p.prices.find((x) => x.currency === f.currency)?.amount ?? 0,
                    f.currency,
                  )}`,
                  value: optionKeyOf(p.sku, p.stripePriceId),
                }))}
                testId={`billing-${KEY}-sku-${i}`}
              />
            </div>
            <div className="w-24">
              <LabeledInput
                id={`${KEY}-qty-${i}`}
                label={i === 0 ? 'Qty' : ''}
                value={row.quantity}
                onChange={(v) =>
                  setRows((s) => s.map((r, j) => (j === i ? { ...r, quantity: v } : r)))
                }
                numeric
                testId={`billing-${KEY}-qty-${i}`}
              />
            </div>
            <Button
              onClick={() =>
                setRows((s) =>
                  s.length === 1 ? [{ optionKey: '', quantity: '1' }] : s.filter((_, j) => j !== i),
                )
              }
              disabled={busy}
              variant="ghost"
              className="cursor-pointer"
              data-testid={`billing-${KEY}-remove-${i}`}
            >
              {'✕'}
            </Button>
          </div>
        ))}
        <div>
          <Button
            onClick={() => setRows((s) => [...s, { optionKey: '', quantity: '1' }])}
            disabled={busy || rows.length >= HARDWARE_MAX_LINES}
            variant="ghost"
            className="cursor-pointer"
            data-testid={`billing-${KEY}-add-line`}
          >
            {'+ Add line'}
          </Button>
        </div>
      </div>

      {!catalogLoading && allProducts.length > 0 && options.length === 0 ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${KEY}-unmapped`}
        >
          {`The catalog has ${allProducts.length} active product${
            allProducts.length === 1 ? '' : 's'
          } but none is priced in ${f.currency.toUpperCase()} with a Stripe product mapped. Add the SKU to STRIPE_HARDWARE_PRODUCTS (and price it in this currency in deviceProducts) before it can be invoiced.`}
        </p>
      ) : null}
      {pruned?.names.length ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${KEY}-pruned`}
        >
          {`Removed ${pruned.names.join(', ')} — ${pruned.reason}.`}
        </p>
      ) : null}
      {duplicateSku ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid={`billing-${KEY}-duplicate`}
        >
          {`${productName(duplicateSku)} is on more than one line — combine it into a single line with a higher quantity.`}
        </p>
      ) : null}

      <LabeledInput
        id={`${KEY}-comment`}
        label="Comment (recorded on the invoice)"
        value={f.comment}
        onChange={(v) => setF((s) => ({ ...s, comment: v }))}
        placeholder="PO 4471 — 10 sticks for the Acme rollout"
        helper={`Required — why this invoice was raised. ${comment.length}/${INVOICE_COMMENT_MAX}`}
        testId={`billing-${KEY}-comment`}
      />
      {overLimit ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid={`billing-${KEY}-overlimit`}
        >
          {`Comment is ${comment.length} characters — Stripe allows at most ${INVOICE_COMMENT_MAX}.`}
        </p>
      ) : null}

      <DocumentAttachment stripeId={stripeId} keyId={KEY} onAttached={setAttachedDocId} />
        </>
      ) : (
        <p
          className="text-xs text-muted-foreground"
          data-testid={`billing-${KEY}-awaiting-customer`}
        >
          {'Enter a customer Stripe ID above — the shipping region, hardware lines, comment and attachment appear once it is filled in.'}
        </p>
      )}
      {/*
        Inside the gate, like the PO / Invoice card's preview. With no customer there are no
        lines to total, so it would only ever read "Hardware $0.00" — a figure that looks like
        a priced invoice of nothing rather than an empty form, and the card already has its own
        awaiting-customer line saying what to do next.
      */}
      {ready ? (
        <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-preview`}>
          {`Hardware ${formatAmount(subtotal, f.currency)}`}
          {' · shipping is added by the server from the catalog'}
          {f.region === 'us' ? ', and Stripe adds US sales tax' : ''}
          {' when the draft is created.'}
        </p>
      ) : null}

      <ActionButtons
        keyId={KEY}
        submitLabel="Create Draft Invoice"
        valid={valid}
        busy={busy}
        onSubmit={submit}
        onClear={clear}
      />
      <ResultLine msg={msg} testId={`billing-${KEY}-result`} />
      <DocLinkLine state={docLink} keyId={KEY} />
      {result ? <HardwareInvoiceOutcome result={result} /> : null}
    </CardShell>
  );
}

/**
 * The created draft, with a link straight to it in Stripe.
 *
 * A DRAFT has no `hosted_invoice_url` — Stripe only mints that at finalization — so the
 * link is the staff-facing dashboard deep link the server already returns. That is also
 * the right target here: the operator's next step is to REVIEW the draft, which is a
 * dashboard action, not something done on the customer-facing invoice page.
 */
function HardwareInvoiceOutcome({ result }: { result: HardwareInvoiceResult }) {
  const KEY = 'create-hardware-invoice';
  return (
    <div className="flex flex-col gap-2" data-testid={`billing-${KEY}-outcome`}>
      {/*
        Strict === false: `undefined` means the feature is off, which is not a failure
        and must render nothing. See the matching banner on the subscription card.
      */}
      {result.finalizeScheduled === false ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${KEY}-finalize-warning`}
        >
          {`⚠ Auto-send was NOT scheduled — this invoice stays a draft until someone finalizes it in Stripe. Do it by hand, or the customer never receives it.`}
        </p>
      ) : null}
      {result.finalizesAt ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs font-medium text-warning"
          data-testid={`billing-${KEY}-finalizes-at`}
        >
          {`⏱ Auto-sends ${formatFinalizeDeadline(result.finalizesAt)}${
            result.finalizeRule === 'opticlaw' ? ' (raised by OptiClaw)' : ''
          } — review it in Stripe before then.`}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-summary`}>
        {[
          result.companyName ?? result.accountId ?? result.invoiceId,
          result.number ? `draft ${result.number}` : 'draft',
          formatAmount(result.total, result.currency),
        ]
          .filter(Boolean)
          .join(' — ')}
        {` · ${result.message}`}
      </p>
      {result.dashboardUrl ? (
        <a
          href={result.dashboardUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium text-primary underline underline-offset-2"
          data-testid={`billing-${KEY}-invoice-link`}
        >
          {result.status === 'draft'
            ? 'Open the draft invoice in Stripe →'
            : 'Open the invoice in Stripe →'}
        </a>
      ) : null}
    </div>
  );
}

// ───────────── Op 7: Create Stripe Subscription (Stripe + Mongo) ─────────────

/** One add-on row. `on` gates the row; a ticked row's fields become required. */
type SubAddOnRow = { on: boolean; screens: string; tier: string };
const emptySubAddOnRow = (): SubAddOnRow => ({ on: false, screens: '', tier: '' });

/**
 * Add-on rows for Create Stripe Subscription.
 *
 * Driven entirely by the SERVER catalog rather than a hardcoded list — the whole
 * point being that what can be sold follows `productPlans`, and the card cannot
 * offer something the subscription could not actually bill.
 *
 * Note what is NOT here: the `si_…` field the Normal Onboarding rows demand. This
 * tool creates the subscription lines, so it reads their item ids back from Stripe.
 * That field is where a mistyped id used to double-bill the customer at their next
 * screen change; here there is nothing to mistype.
 */
function SubscriptionAddOnSection({
  addOns,
  state,
  planFeature,
  currency,
  onChange,
  enabled,
  onToggle,
}: {
  addOns: PlanCatalogAddOn[];
  state: Record<string, SubAddOnRow>;
  planFeature: string;
  currency: string;
  onChange: (key: string, patch: Partial<SubAddOnRow>) => void;
  /** Closed by default — the rows only exist once the operator opts in. */
  enabled: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-dashed p-3"
      data-testid="billing-sub-addons"
    >
      <div className="flex flex-col gap-0.5">
        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={enabled}
            onCheckedChange={(v) => onToggle(v === true)}
            className="cursor-pointer"
            data-testid="billing-sub-addons-enable"
          />
          <span className="text-xs font-medium text-foreground">{'Add-ons (optional)'}</span>
        </label>
        <p className="text-[11px] text-muted-foreground">
          {
            'Each ticked add-on becomes its own line on the new subscription. Its Stripe subscription-item id is read back from Stripe — you never have to paste one.'
          }
        </p>
      </div>
      {/* Closed ⇒ no rows at all. The parent CLEARS the state on untick, so nothing can be
          submitted from a section the operator can no longer see. */}
      {!enabled ? null : addOns.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{'No add-ons in the plan catalog.'}</p>
      ) : null}
      {enabled && addOns.map((a) => {
        const row = state[a.key] ?? emptySubAddOnRow();
        const planBlocked =
          a.requiresPlan.length > 0 && !!planFeature && !a.requiresPlan.includes(planFeature);
        const notOnPlan = a.plans.length > 0 && !!planFeature && !a.plans.includes(planFeature);
        const price = a.prices.find((p) => p.currency === currency);
        const unpriced = !!currency && !price;
        return (
          <div key={a.key} className="flex flex-col gap-1">
            {/*
              Row shape matches the PO / Invoice - Change Screens / Add-ons card: the tick and the label on
              their own line, then the controls indented beneath. The label used to be a fixed
              `w-40` column with the input beside it, which left a wide gap on short labels and
              put the two add-on sections in this console at different positions.
            */}
            <label className="flex cursor-pointer items-center gap-2 text-xs">
              <Checkbox
                checked={row.on}
                // Ticking a tiered add-on lands on Standard rather than an empty "Tier" box.
                // Standard is the common case, and an unset tier blocks submit — so the empty
                // state was a required field disguised as an optional one.
                onCheckedChange={(v) =>
                  onChange(a.key, {
                    on: v === true,
                    ...(v === true && a.tiers.length && !row.tier ? { tier: 'standard' } : {}),
                  })
                }
                className="cursor-pointer"
                data-testid={`billing-sub-addon-${a.key}-toggle`}
              />
              <span>{a.label}</span>
            </label>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pl-6">
              {row.on ? (
                <>
                  {a.quantified ? (
                    <Input
                      value={row.screens}
                      onChange={(e) =>
                        onChange(a.key, { screens: e.target.value.replace(/[^0-9]/g, '') })
                      }
                      placeholder="Screens"
                      // Matches the PO / Invoice - Change Screens / Add-ons card's add-on input, so the two
                      // add-on sections in this console are the same control at the same size.
                      className="w-40"
                      aria-label={`${a.label} screens`}
                      data-testid={`billing-sub-addon-${a.key}-screens`}
                    />
                  ) : null}
                  {a.tiers.length ? (
                    <Select value={row.tier} onValueChange={(v) => onChange(a.key, { tier: v })}>
                      <SelectTrigger
                        className="w-32 cursor-pointer"
                        aria-label={`${a.label} tier`}
                        data-testid={`billing-sub-addon-${a.key}-tier`}
                      >
                        <SelectValue placeholder="Tier" />
                      </SelectTrigger>
                      <SelectContent>
                        {a.tiers.map((t) => (
                          <SelectItem key={t} value={t} className="cursor-pointer">
                            {t === 'pro' ? 'Pro' : 'Standard'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : null}
                  <span className="text-[11px] text-muted-foreground">
                    {/* An auto-converted price's real amount lives in Stripe, so we say so
                        rather than showing the catalog's USD figure under another label. */}
                    {price?.autoConverted
                      ? `billed at Stripe’s ${currency.toUpperCase()} rate`
                      : price?.amountMinor != null
                        ? `${formatAmount(price.amountMinor, currency)} / screen / year`
                        : ''}
                  </span>
                </>
              ) : null}
            </div>
            {row.on && planBlocked ? (
              <p
                className="pl-6 text-[11px] text-destructive"
                data-testid={`billing-sub-addon-${a.key}-planError`}
              >
                {`Requires the ${a.requiresPlan.join(' or ')} plan — the selected plan does not grant it.`}
              </p>
            ) : null}
            {row.on && !planBlocked && notOnPlan ? (
              <p
                className="pl-6 text-[11px] text-destructive"
                data-testid={`billing-sub-addon-${a.key}-catalogError`}
              >
                {`The ${planFeature} plan’s catalog entry does not carry this add-on — it cannot be priced on this plan.`}
              </p>
            ) : null}
            {row.on && unpriced ? (
              <p
                className="pl-6 text-[11px] text-destructive"
                data-testid={`billing-sub-addon-${a.key}-currencyError`}
              >
                {`Not priced in ${currency.toUpperCase()} in the plan catalog.`}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** The five billing-address fields, in Stripe's own terms. */
type AddressState = {
  country: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
};
const emptyAddress = (): AddressState => ({
  country: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
});

/**
 * Billing address for the Stripe CUSTOMER — the one customer field this tool writes.
 *
 * Prefilled from Stripe as soon as a `cus_` id is entered, which is a safety property
 * rather than a convenience: an operator sees the address Stripe already holds before
 * replacing it, so a good address cannot be clobbered unseen. Leaving the prefilled
 * values alone sends nothing at all.
 *
 * It is here because `automatic_tax` resolves tax from this address, and Stripe REFUSES
 * a subscription for a customer it cannot locate — so when Stripe has no usable address
 * these fields are required, and the card says so instead of letting submit fail.
 */
function BillingAddressSection({
  keyId,
  address,
  onChange,
  loading,
  known,
  dirty,
  open,
  onToggle,
}: {
  keyId: string;
  address: AddressState;
  onChange: (patch: Partial<AddressState>) => void;
  loading: boolean;
  /** What Stripe currently holds; null until a customer has been looked up. */
  known: StripeCustomerBilling | null;
  dirty: boolean;
  /** Collapsed when Stripe already holds a usable address — see the caller's effect. */
  open: boolean;
  onToggle: (open: boolean) => void;
}) {
  const country = address.country.trim().toUpperCase();
  const subdivisions = SUBDIVISIONS_BY_COUNTRY[country];
  const required = !!known && !known.hasAddress;

  /**
   * The collapsed view. Every part of the address the operator would check is here, in the
   * order they would read it, because collapsing must not HIDE the address — the whole safety
   * property of this section is that a good address cannot be replaced unseen.
   */
  const summary = [
    address.line1.trim(),
    address.line2.trim(),
    address.city.trim(),
    [address.state.trim().toUpperCase(), address.postalCode.trim()].filter(Boolean).join(' '),
    country,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-dashed p-3"
      data-testid={`${keyId}-address`}
    >
      <div className="flex flex-col gap-0.5">
        {/* The whole row toggles, not just the chevron — a 12px glyph is a poor target for
            something used on every onboarding. */}
        <button
          type="button"
          onClick={() => onToggle(!open)}
          className="flex w-full cursor-pointer items-center justify-between gap-2 text-left"
          aria-expanded={open}
          data-testid={`${keyId}-address-toggle`}
        >
          <p className="text-xs font-medium text-foreground">
            {'Customer billing address'}
            {required ? ' (required)' : ' (optional)'}
          </p>
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {open ? '▲' : '▼'}
          </span>
        </button>
        {/* Collapsed: the address itself, on one line. Shown INSTEAD of the explanatory copy,
            which is about editing — there is nothing to explain until it is open. */}
        {!open ? (
          <p className="text-[11px] text-muted-foreground" data-testid={`${keyId}-address-summary`}>
            {summary || 'No address on file.'}
          </p>
        ) : null}
        {open ? (
        <p className="text-[11px] text-muted-foreground">
          {loading
            ? 'Reading the customer’s current address from Stripe…'
            : required
              ? 'This customer has no usable address in Stripe. Stripe calculates tax from it, so it will refuse the subscription until one is set.'
              : known
                ? 'Prefilled from Stripe. Edit only what is wrong — leave it as-is and nothing is written.'
                : 'Enter the customer Stripe ID above and this fills in from Stripe.'}
        </p>
        ) : null}
        {/* Shown in BOTH states on purpose: an operator who edits the address and then folds
            the section must not lose the one line saying Stripe will be written to. */}
        {dirty ? (
          <p className="text-[11px] text-warning" data-testid={`${keyId}-address-dirty`}>
            {'This will overwrite the address on the Stripe customer.'}
          </p>
        ) : null}
      </div>
      {open ? (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* A picker, not a text field: Stripe's tax engine keys off the ISO code, and a
            typed country NAME is accepted by its API and then silently fails to resolve
            tax. Ordered like Stripe's own list (United States first) because that is
            where the operator will cross-check the address. */}
        <LabeledSelect
          id={`${keyId}-country`}
          label="Billing Country"
          value={address.country.trim().toUpperCase()}
          onChange={(v) => onChange({ country: v })}
          placeholder="Choose a country…"
          options={BILLING_COUNTRIES.map((c) => ({ label: c.name, value: c.code }))}
          helper={
            country
              ? `${BILLING_COUNTRY_NAME[country] ?? country} (${country}) — Stripe resolves tax from this.`
              : 'Stripe resolves tax from the country, state and zipcode.'
          }
          testId={`${keyId}-country`}
        />
        <LabeledInput
          id={`${keyId}-line1`}
          label="Address"
          value={address.line1}
          onChange={(v) => onChange({ line1: v })}
          placeholder="Address line 1"
          testId={`${keyId}-line1`}
        />
        <LabeledInput
          id={`${keyId}-line2`}
          label="Suite / Apt (optional)"
          value={address.line2}
          onChange={(v) => onChange({ line2: v })}
          placeholder="Address line 2"
          helper="Never required — tax is resolved from country, state and zipcode."
          testId={`${keyId}-line2`}
        />
        <LabeledInput
          id={`${keyId}-city`}
          label="City"
          value={address.city}
          onChange={(v) => onChange({ city: v })}
          placeholder="City"
          testId={`${keyId}-city`}
        />
        {/* A picker wherever tax is subdivision-level, populated from the chosen
            country — so "California" can never be sent where "CA" is meant. Everywhere
            else it stays free text: most countries have no subdivision Stripe uses, and
            a picker there would imply tax depends on it. */}
        {subdivisions ? (
          <LabeledSelect
            id={`${keyId}-state`}
            label={country === 'CA' ? 'Province' : 'State'}
            value={address.state.trim().toUpperCase()}
            onChange={(v) => onChange({ state: v })}
            placeholder={country === 'CA' ? 'Choose a province…' : 'Choose a state…'}
            options={subdivisions.map((sub) => ({ label: sub.name, value: sub.code }))}
            helper={`Required for ${country} — tax is ${
              country === 'CA' ? 'province' : 'state'
            }-level there.`}
            testId={`${keyId}-state`}
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${keyId}-state`} className="text-xs">
              {'State'}
            </Label>
            <Input
              id={`${keyId}-state`}
              value={address.state}
              onChange={(e) => onChange({ state: e.target.value })}
              placeholder="State"
              data-testid={`${keyId}-state`}
            />
            <p className="text-[11px] text-muted-foreground">
              {'Not used for tax outside the US and Canada; leave blank if there is none.'}
            </p>
          </div>
        )}
        <LabeledInput
          id={`${keyId}-postalCode`}
          label="Zipcode"
          value={address.postalCode}
          onChange={(v) => onChange({ postalCode: v })}
          placeholder="ZIP (95014)"
          testId={`${keyId}-postalCode`}
        />
      </div>
      ) : null}
    </div>
  );
}

/**
 * Creates a Stripe subscription from the plan catalog, then writes the same company
 * fields Normal Onboarding writes — using the ids Stripe just returned.
 *
 * This is the only Billing Console card that touches BOTH Stripe and Mongo, which is
 * also why its result is a panel rather than a line: it has to show the ids it created,
 * a clickable link into the still-draft first invoice, and — in the one bad case —
 * that Stripe succeeded while Mongo did not.
 */
function CreateStripeSubscriptionForm({
  stripeId,
  customer,
}: {
  /** The console's shared customer — this card no longer owns an id field. */
  stripeId: string;
  /**
   * ALSO this card's address baseline. The console already resolves
   * `staffBillingStripeCustomer`, which is exactly the read this card used to make for itself
   * — so the duplicate lookup is gone and `knownBilling` is the shared value.
   */
  customer: StripeCustomerBilling | null;
}) {
  const KEY = 'create-stripe-subscription';
  const empty = { planFeature: '', subQty: '', currency: '', comment: '', billingEmail: '' };
  const [f, setF] = useState({ ...empty });
  const [addOns, setAddOns] = useState<Record<string, SubAddOnRow>>({});
  const [address, setAddress] = useState<AddressState>(emptyAddress);
  /** What Stripe holds right now — the baseline the form is compared against, shared. */
  const knownBilling = customer;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [result, setResult] = useState<StripeSubscriptionResult | null>(null);
  // Set when a file is attached; used to back-fill the invoice id once the create
  // succeeds. Cleared with the form so a later invoice cannot adopt an old document.
  const [attachedDocId, setAttachedDocId] = useState<string | null>(null);
  /** Whether that document ended up cross-referenced — surfaced, not swallowed. */
  const [docLink, setDocLink] = useState<DocLinkState>({ kind: 'none' });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const setAddOn = (key: string, patch: Partial<SubAddOnRow>) =>
    setAddOns((s) => ({ ...s, [key]: { ...(s[key] ?? emptySubAddOnRow()), ...patch } }));

  const { data: catalogData, loading: catalogLoading, error: catalogError } =
    useBillingPlanCatalog();
  const [createSubscription] = useBillingCreateStripeSubscription();

  /**
   * Devices billed on this subscription's FIRST invoice — one bill for the customer.
   *
   * The currency is the SUBSCRIPTION's, not a separate choice: the plan and the hardware land
   * on one invoice, and Stripe has no way to mix currencies on it. `deviceRegion` is its own
   * control because it selects the freight branch and nothing else on this card implies it.
   */
  const [deviceRegion, setDeviceRegion] = useState<HardwareRegion>('us');
  const devices = useDeviceLines({ currency: f.currency, region: deviceRegion });

  /**
   * Both optional sections start CLOSED and are opened by their own checkbox.
   *
   * Unticking CLEARS the section rather than merely hiding it. Hidden-but-submitted data is the
   * hazard here: an operator who adds hardware, changes their mind, unticks and submits must not
   * be billing that hardware from a collapsed section they can no longer see.
   */
  const [addOnsEnabled, setAddOnsEnabled] = useState(false);
  const [devicesEnabled, setDevicesEnabled] = useState(false);
  /**
   * The address section, unlike the two above, is not a CHOICE — it is a fact about the
   * customer that is usually already correct. So it is collapsed rather than gated: no
   * checkbox, nothing cleared on close, and the summary keeps the address on screen.
   */
  const [addressOpen, setAddressOpen] = useState(true);

  const toggleAddOns = (on: boolean) => {
    setAddOnsEnabled(on);
    if (!on) setAddOns({});
  };
  const toggleDevices = (on: boolean) => {
    setDevicesEnabled(on);
    if (!on) {
      devices.reset();
      setDeviceRegion('us');
    }
  };

  /**
   * Patch the address, dropping a state the NEW country cannot have.
   *
   * Without this, switching United States → Canada would keep `CA` (California) and send
   * it as a province — a valid-looking code that is not a Canadian one. Only fires on an
   * explicit country change, so it never interferes with the prefill (which sets country
   * and state together from Stripe).
   */
  const changeAddress = (patch: Partial<AddressState>) =>
    setAddress((a) => {
      const next = { ...a, ...patch };
      if (patch.country !== undefined && patch.country !== a.country) {
        const allowed = SUBDIVISIONS_BY_COUNTRY[next.country.trim().toUpperCase()];
        // Cleared when the new country has a list this code is not in, and when it has
        // no list at all (a state is meaningless there).
        if (!allowed || !allowed.some((sub) => sub.code === next.state.trim().toUpperCase())) {
          next.state = '';
        }
      }
      return next;
    });

  /**
   * Prefill the address from Stripe once a plausible `cus_` id has been entered.
   *
   * Fired on blur rather than per keystroke: it is a network round trip per call, and a
   * half-typed id is never a real customer. A failure is deliberately quiet — a wrong id
   * is caught properly at submit, and a red banner here would fire while someone is
   * still mid-edit.
   */
  /**
   * Seed the address fields when the shared customer resolves.
   *
   * Replaces this card's own lookup + blur handler + debounce, all of which duplicated the
   * console's read. Keyed on the customer's id rather than the object so a re-render with an
   * equal-but-new object cannot wipe an address the operator has started editing.
   */
  useEffect(() => {
    if (!customer) {
      setAddress(emptyAddress());
      setAddressOpen(true);
      setF((s) => ({ ...s, billingEmail: '' }));
      return;
    }
    // Prefilled for the same reason as the address: the operator SEES what Stripe already
    // sends invoices to before replacing it, so a good address cannot be clobbered unseen.
    setF((s) => ({ ...s, billingEmail: customer.billingEmail ?? '' }));
    setAddress({
      country: customer.country ?? '',
      line1: customer.line1 ?? '',
      line2: customer.line2 ?? '',
      city: customer.city ?? '',
      state: customer.state ?? '',
      postalCode: customer.postalCode ?? '',
    });
    // Six fields the operator has nothing to do with are six fields of noise between the plan
    // and the comment — so a prefilled address folds to one line. It opens itself when Stripe
    // has NO usable address, because then it is required and `automatic_tax` will refuse the
    // subscription until it is filled in: the one case where these fields are the operator's
    // next action rather than a fact to glance at.
    setAddressOpen(!customer.hasAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.stripeId]);

  /**
   * Everything below the plan picker stays hidden until a customer id has been ENTERED.
   *
   * Deliberately a weaker gate than the PO / Invoice - Change Screens / Add-ons card's, which waits for a
   * resolved subscription. The two cards differ in what their fields depend on:
   *
   *   - There, the fields are DERIVED from the read — add-on rows are seeded from what the
   *     customer holds and the delta is applied to their current quantity. Rendering them
   *     before the read lands would show a wrong state, so the read is the gate.
   *   - Here, nothing below is derived from the customer. Quantity is free, currency comes
   *     from the PLAN, add-ons come from the plan catalog, and the address is prefilled from
   *     Stripe but remains hand-enterable — which is the whole point for a customer Stripe
   *     holds no address for.
   *
   * So gating this card on a successful lookup would make it unusable whenever the lookup
   * fails or the Stripe key is unset, where today an operator can still complete the form by
   * hand. The id being present is enough to collapse the card and save the space.
   */
  const ready = /^cus_\w{6,}$/i.test(stripeId.trim());

  const catalog = catalogData?.staffBillingPlanCatalog;
  const plans = catalog?.plans ?? [];
  const catalogAddOns = catalog?.addOns ?? [];

  const selectedPlan = plans.find((p) => p.alias === f.planFeature);
  /** Only currencies the SELECTED plan is priced in — the rest would be refused. */
  const planCurrencies = selectedPlan?.prices.map((p) => p.currency) ?? catalog?.currencies ?? [];
  const planPrice = selectedPlan?.prices.find((p) => p.currency === f.currency);

  const qty = parseInt(f.subQty, 10) || 0;
  const comment = f.comment.trim();
  const overLimit = comment.length > INVOICE_COMMENT_MAX;

  /** Display only: the server re-reads every price, and Stripe's invoice is the truth. */
  const planUnit = planPrice?.amountMinor ?? null;
  const planSubtotal = planUnit != null ? planUnit * qty : null;

  // A closed section contributes nothing, even if state somehow survived — the gate reads the
  // flag, not just the rows.
  const ticked = addOnsEnabled ? catalogAddOns.filter((a) => addOns[a.key]?.on) : [];
  const addOnsValid = ticked.every((a) => {
    const row = addOns[a.key];
    if (a.quantified && !(parseInt(row.screens, 10) > 0)) return false;
    if (a.tiers.length && !row.tier) return false;
    if (a.requiresPlan.length && !a.requiresPlan.includes(f.planFeature)) return false;
    if (a.plans.length && !a.plans.includes(f.planFeature)) return false;
    // Refused server-side too; blocking here keeps the operator out of a round trip.
    if (!a.prices.some((p) => p.currency === f.currency)) return false;
    return true;
  });

  // Compared field by field against what Stripe holds: an untouched prefill must send
  // nothing, so that merely opening the card cannot rewrite a customer's address.
  const addressDirty =
    !!knownBilling &&
    ((address.country.trim().toUpperCase() !== (knownBilling.country ?? '').toUpperCase()) ||
      address.line1.trim() !== (knownBilling.line1 ?? '') ||
      address.line2.trim() !== (knownBilling.line2 ?? '') ||
      address.city.trim() !== (knownBilling.city ?? '') ||
      address.state.trim().toUpperCase() !== (knownBilling.state ?? '').toUpperCase() ||
      address.postalCode.trim() !== (knownBilling.postalCode ?? ''));

  /**
   * Billing email — the Stripe customer's own, i.e. where Stripe sends their invoices.
   *
   * Dirty-checked against what Stripe already holds so an untouched prefill costs no write,
   * exactly like the address. Trimmed on both sides of the comparison: a stray space is not
   * an edit, and sending one would rewrite the customer for nothing.
   */
  const billingEmail = f.billingEmail.trim();
  const billingEmailDirty = billingEmail !== (knownBilling?.billingEmail ?? '').trim();
  // Shape-checked in the form so an obvious typo is caught before it reaches Stripe — Stripe
  // remains the authority on what it will accept. Blank is NOT valid to send: Stripe reads ''
  // as "clear the email", which would leave the customer with no invoice recipient at all.
  const billingEmailUsable = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail);
  const billingEmailBad = billingEmailDirty && !billingEmailUsable;

  const addressCountry = address.country.trim().toUpperCase();
  const addressComplete =
    /^[A-Z]{2}$/.test(addressCountry) &&
    address.line1.trim().length > 0 &&
    address.city.trim().length > 0 &&
    address.postalCode.trim().length > 0 &&
    (!STATE_REQUIRED_COUNTRIES.includes(addressCountry) ||
      /^[A-Z]{2}$/.test(address.state.trim().toUpperCase()));

  /**
   * Stripe refuses a subscription for a customer whose location it cannot resolve, so
   * an address is mandatory when Stripe has none. Otherwise it is optional — but a
   * PARTIAL edit is never acceptable, since a half-written address is worse than the one
   * it replaced.
   */
  const addressValid = knownBilling
    ? knownBilling.hasAddress
      ? !addressDirty || addressComplete
      : addressComplete
    : true;

  // Devices are OPTIONAL here — most onboardings are software only. But a half-filled set
  // (a duplicate configuration, or a quantity of 0) must block submit rather than reach the
  // server, so the gate is "no device rows at all, OR a valid set of them".
  //
  // Plus the cap: a device drives a screen, so more devices than the plan licenses leaves the
  // customer with hardware they cannot activate yet. A WARNING, not a block (2026-09-25): spares,
  // replacements and screens licensed later are legitimate reasons to over-order, so the operator
  // is told and the call is theirs. The server accepts it too.
  const deviceRows = devicesEnabled ? devices.filledRows : [];
  const deviceQuantity = deviceRows.reduce((sum, r) => sum + (parseInt(r.quantity, 10) || 0), 0);
  const overScreenLimit = qty > 0 && deviceQuantity > qty;
  const devicesOk = deviceRows.length === 0 || devices.linesValid;

  const valid =
    /^cus_/i.test(stripeId.trim()) &&
    !!f.planFeature &&
    qty > 0 &&
    !!f.currency &&
    planCurrencies.includes(f.currency) &&
    comment.length > 0 &&
    !overLimit &&
    addOnsValid &&
    addressValid &&
    // A malformed EDIT blocks; leaving the prefill alone never does, even if what Stripe
    // already holds is odd — this card is not the place to force that cleanup.
    !billingEmailBad &&
    devicesOk;

  /** Changing plan can invalidate the currency — a plan is not priced in all five. */
  const changePlan = (planFeature: string) => {
    const next = plans.find((p) => p.alias === planFeature);
    const stillPriced = !!next?.prices.some((p) => p.currency === f.currency);
    // A plan change can CLEAR the currency, and device lines are priced in it — so prune the
    // lines that no longer have a price rather than carrying them to a server refusal.
    if (!stillPriced) devices.pruneForCurrency('');
    setF((s) => ({ ...s, planFeature, currency: stillPriced ? s.currency : '' }));
  };

  /** Currency is the subscription's; changing it re-prices the device lines too. */
  const changeCurrency = (currency: string) => {
    devices.pruneForCurrency(currency);
    setF((s) => ({ ...s, currency }));
  };

  const changeDeviceRegion = (region: HardwareRegion) => {
    devices.pruneForRegion(region);
    setDeviceRegion(region);
  };

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    setResult(null);
    try {
      const r = (
        await createSubscription({
          variables: {
            input: {
              stripeId: stripeId.trim(),
              planFeature: f.planFeature,
              subQty: qty,
              currency: f.currency,
              ...(ticked.length
                ? {
                    addOns: ticked.map((a) => ({
                      key: a.key,
                      ...(a.quantified
                        ? { maxQuantity: parseInt(addOns[a.key].screens, 10) }
                        : {}),
                      ...(a.tiers.length ? { tier: addOns[a.key].tier } : {}),
                    })),
                  }
                : {}),
              // Sent ONLY when the operator actually changed something. An unchanged
              // prefill costs no Stripe write.
              ...(addressDirty
                ? {
                    billingAddress: {
                      country: addressCountry,
                      line1: address.line1.trim(),
                      // Sent even when blank IF the operator cleared a suite number that
                      // Stripe had — otherwise Stripe would keep the old one.
                      ...(address.line2.trim() || knownBilling?.line2
                        ? { line2: address.line2.trim() }
                        : {}),
                      city: address.city.trim(),
                      ...(address.state.trim()
                        ? { state: address.state.trim().toUpperCase() }
                        : {}),
                      postalCode: address.postalCode.trim(),
                    },
                  }
                : {}),
              // Same rule as the address: sent ONLY when the operator changed it.
              ...(billingEmailDirty && billingEmailUsable ? { billingEmail } : {}),
              comment,
              // Catalog references and counts only — never money. The server re-reads every
              // amount from the device catalog, same rule as Create Hardware Only Invoice.
              ...(deviceRows.length
                ? { deviceLines: devices.payloadLines(), deviceRegion }
                : {}),
              // See the hardware card: this is what puts the document link on the invoice.
              ...(attachedDocId ? { documentId: attachedDocId } : {}),
            },
          },
        })
      ).data!.staffBillingCreateStripeSubscription;
      setResult(r);
      // A new subscription is an increase from nothing, so the quantity IS the increase.
      setDocLink(await linkInvoiceToDocument(attachedDocId, r.invoiceId, r.invoiceUrl, qty));
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    setAttachedDocId(null);
    setDocLink({ kind: 'none' });
    setF({ ...empty });
    setAddOns({});
    // Device lines are reset too. They are per-customer and per-currency, so carrying them
    // across a Clear would bill the NEXT customer for hardware chosen for this one.
    devices.reset();
    setDeviceRegion('us');
    // Re-seed rather than blank: Clear resets the card to its initial state FOR THE CURRENT
    // CUSTOMER, and that includes the address Stripe holds. Emptying it would leave the card
    // blank until the customer changed, since the seed effect is keyed on the customer's id.
    setAddress(
      customer
        ? {
            country: customer.country ?? '',
            line1: customer.line1 ?? '',
            line2: customer.line2 ?? '',
            city: customer.city ?? '',
            state: customer.state ?? '',
            postalCode: customer.postalCode ?? '',
          }
        : emptyAddress(),
    );
        setMsg(null);
    setResult(null);
  };

  return (
    <CardShell
      testId={`billing-card-${KEY}`}
      // Also written to Stripe as `optisigns_staff_action` on the subscription and its
      // first invoice (see stripe-subscription.client.ts META_ACTION_VALUE) — rename
      // both together, or the Stripe dashboard names a card that no longer exists.
      title="PO / Invoice - Onboarding Process"
      description="Creates the customer's yearly Stripe subscription from the plan catalog, then onboards the account in Mongo with the ids Stripe returns — the Stripe half of Normal Onboarding, done for you. Its first invoice is created as a DRAFT and then SENDS ITSELF: Stripe finalizes and emails it to the customer at the deadline shown after you create it (about an hour when you raise it yourself), so use the link below to review it before then. Paid by invoice (net 30) — no saved card is ever charged. Your comment becomes that invoice's memo; it is not stored on the subscription, so renewals do not repeat it."
    >
      {catalogError ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid={`billing-${KEY}-catalog-error`}
        >
          {`Could not load the plan catalog: ${catalogError.message}`}
        </p>
      ) : null}


      {ready ? (
        <>
          {/*
            All three of the deal's terms in ONE two-column grid, so they read as one row plus
            a remainder rather than as three separate blocks:
            
              Plan Feature  |  Subscription Quantity
              Currency      |
            
            Plan Feature moved INSIDE the gate to make that possible — a conditional cannot
            wrap half a grid without leaving an unbalanced tree, and a plan picker is no use
            before a customer is named anyway.
          */}
          <div className="grid gap-3 sm:grid-cols-2">
          <LabeledSelect
            id={`${KEY}-planFeature`}
            label="Plan Feature"
            value={f.planFeature}
            onChange={changePlan}
            placeholder={catalogLoading ? 'Loading…' : 'Select plan feature'}
            options={plans.map((p) => ({ label: p.name, value: p.alias }))}
            helper="From productPlans — the same catalog the portal prices from."
            testId={`billing-${KEY}-planFeature`}
          />
          <LabeledInput
            id={`${KEY}-subQty`}
            label="Subscription Quantity"
            value={f.subQty}
            onChange={set('subQty')}
            placeholder="10"
            numeric
            helper="Screens on the plan line. Also sets the account limit and 10 GB storage per screen."
            testId={`billing-${KEY}-subQty`}
          />
          <LabeledSelect
            id={`${KEY}-currency`}
            label="Currency"
            value={f.currency}
            // NOT `set('currency')`: device lines are priced in this currency, so changing it
            // has to prune the ones that no longer have a price and say which.
            onChange={changeCurrency}
            placeholder={f.planFeature ? 'Select currency' : 'Select a plan first'}
            options={CURRENCIES.filter((c) => planCurrencies.includes(c.value))}
            helper="Only currencies this plan is priced in."
            testId={`billing-${KEY}-currency`}
          />
          {/* Fourth cell of the two-column grid, so it sits to the RIGHT of Currency. */}
          <LabeledInput
            id={`${KEY}-billingEmail`}
            label="Billing Email"
            value={f.billingEmail}
            onChange={set('billingEmail')}
            placeholder={customer ? 'billing@customer.com' : 'Enter a customer id first'}
            helper={
              billingEmailBad
                ? 'That is not a usable email — Stripe sends every invoice for this customer to it.'
                : billingEmailDirty
                  ? 'Will be SAVED to the Stripe customer — this redirects every future invoice for them, not just this one.'
                  : customer?.billingEmail
                    ? 'From Stripe. Edit to change where this customer’s invoices are sent.'
                    : 'Stripe holds no billing email for this customer — invoices have nowhere to go until one is set.'
            }
            testId={`billing-${KEY}-billingEmail`}
          />
        </div>

        <BillingAddressSection
          keyId={`billing-${KEY}`}
          address={address}
          onChange={changeAddress}
          // The console owns the lookup now; by the time this card renders an address there
          // is nothing left for it to wait on.
          loading={false}
          known={knownBilling}
          dirty={addressDirty}
          open={addressOpen}
          onToggle={setAddressOpen}
        />

        <SubscriptionAddOnSection
          addOns={catalogAddOns}
          state={addOns}
          planFeature={f.planFeature}
          currency={f.currency}
          onChange={setAddOn}
          enabled={addOnsEnabled}
          onToggle={toggleAddOns}
        />

        {/* Devices on this subscription's FIRST invoice. Optional: leave it empty for a
            software-only onboarding and nothing changes. The picker and every rule about what
            may be offered come from the same hook Create Hardware Only Invoice uses, so the two
            cards cannot disagree about pricing or about what a region change invalidates. */}
        {/* Presented like Add-ons (optional) above — same dashed container, same title +
            [11px] description block — because it is the same kind of thing: an optional
            addition to the deal being built. */}
        <div
          className="flex flex-col gap-2 rounded-md border border-dashed p-3"
          data-testid="billing-sub-devices"
        >
          <div className="flex flex-col gap-0.5">
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={devicesEnabled}
                onCheckedChange={(v) => toggleDevices(v === true)}
                className="cursor-pointer"
                data-testid={`billing-${KEY}-devices-enable`}
              />
              <span className="text-xs font-medium text-foreground">{'Hardware (optional)'}</span>
            </label>
            <p className="text-[11px] text-muted-foreground">
              {
                'Billed on this subscription’s FIRST invoice, so the customer receives one bill. Priced live from the device catalog. Hardware is never discountable, so a coupon on this account reduces the plan only. At most one device per licensed screen.'
              }
            </p>
          </div>
          {devicesEnabled ? (
          <>
          <div className="w-[220px]">
            <LabeledSelect
              id={`${KEY}-deviceRegion`}
              label="Ship to"
              value={deviceRegion}
              onChange={(v: string) => changeDeviceRegion(v as HardwareRegion)}
              placeholder="Select region"
              options={SHIP_REGIONS}
              testId={`billing-${KEY}-deviceRegion`}
            />
          </div>
          <DeviceLineRows
            keyId={KEY}
            rows={devices.rows}
            setRows={devices.setRows}
            options={devices.options}
            allProducts={devices.allProducts}
            currency={f.currency}
            catalogLoading={devices.catalogLoading}
            pruned={devices.pruned}
            disabled={busy}
            label=""
            formatAmount={formatAmount}
            LabeledSelect={LabeledSelect}
            LabeledInput={LabeledInput}
          />
          {devices.duplicateSku ? (
            <p className="text-xs text-destructive" data-testid={`billing-${KEY}-device-duplicate`}>
              {'The same configuration is on two lines — combine them into one quantity.'}
            </p>
          ) : null}
          {/* No currency yet ⇒ nothing can be priced ⇒ the picker is EMPTY, which reads as
              broken. Say why, and point at the tool for buying devices on their own: this card
              bills them on a NEW subscription's first invoice, and the device cap measures
              against that subscription's screen count, so it cannot work without a plan.
              Keyed on the currency rather than on filled rows — a row cannot even be picked
              before a currency exists, so that condition was unreachable. */}
          {!f.currency ? (
            <p
              className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
              data-testid={`billing-${KEY}-devices-need-plan`}
            >
              {
                'Buying devices without a subscription? Use Create Hardware Only Invoice instead — this card bills devices on a NEW subscription’s first invoice, so it needs a plan.'
              }
            </p>
          ) : null}
          {overScreenLimit ? (
            <p className="text-xs text-warning" data-testid={`billing-${KEY}-device-over-limit`}>
              {`${deviceQuantity} device${deviceQuantity === 1 ? '' : 's'} for ${qty} screen${
                qty === 1 ? '' : 's'
              } — a device without a screen licence cannot be activated. You can still submit if that is intended (spares, replacements).`}
            </p>
          ) : null}
          {devices.filledRows.length > 0 ? (
            <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-device-subtotal`}>
              {`Hardware subtotal ${formatAmount(devices.subtotal, f.currency)} — shipping and tax are added by the server.`}
            </p>
          ) : null}
          </>
          ) : null}
        </div>

        <LabeledInput
          id={`${KEY}-comment`}
          label="Comment (the first invoice's memo)"
          value={f.comment}
          onChange={set('comment')}
          placeholder="PO 4471 — Acme 3-year deal, year 1"
          helper={`Required — why this subscription was raised. Written as the MEMO on the first invoice only, so it is customer-visible there but does not repeat on renewals. ${comment.length}/${INVOICE_COMMENT_MAX}`}
          testId={`billing-${KEY}-comment`}
        />
        {overLimit ? (
          <p
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            data-testid={`billing-${KEY}-overlimit`}
          >
            {`Comment is ${comment.length} characters — Stripe allows at most ${INVOICE_COMMENT_MAX}.`}
          </p>
        ) : null}

        <DocumentAttachment stripeId={stripeId} keyId={KEY} onAttached={setAttachedDocId} />
        </>
      ) : (
        <p
          className="text-xs text-muted-foreground"
          data-testid={`billing-${KEY}-awaiting-customer`}
        >
          {'Enter a customer Stripe ID above to load them from Stripe — the quantity, currency, billing address, add-ons and memo appear once they resolve.'}
        </p>
      )}

      {/*
        Inside the gate, unlike the Apply Coupon card's preview which stays outside it. The
        difference is what each says with no customer: the coupon one reads "Enter the customer
        and pick a coupon", which IS the guidance a collapsed card needs, while this one talks
        about a plan and quantity that are not on screen yet — and the card already has its own
        awaiting-customer line saying what to do.
      */}
      {ready ? (
        <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-preview`}>
          {planSubtotal != null && planUnit != null && qty > 0
            ? `Screens ${formatAmount(planSubtotal, f.currency)} / year (${qty} × ${formatAmount(
                planUnit,
                f.currency,
              )})`
            : 'Pick a plan, quantity and currency to see the yearly figure'}
          {ticked.length ? ` · plus ${ticked.length} add-on line${ticked.length === 1 ? '' : 's'}` : ''}
          {'. Stripe adds tax at finalization and its invoice is the authoritative total.'}
        </p>
      ) : null}

      <ActionButtons
        keyId={KEY}
        submitLabel="Create Subscription"
        valid={valid}
        busy={busy}
        onSubmit={submit}
        onClear={clear}
      />
      <ResultLine msg={msg} testId={`billing-${KEY}-result`} />
      <DocLinkLine state={docLink} keyId={KEY} />
      {result ? <SubscriptionResult result={result} /> : null}
    </CardShell>
  );
}

/**
 * Row state for the change card's add-on section.
 *
 * A DELIBERATE sibling of SubscriptionAddOnSection rather than a reuse of it. That component
 * is driven purely by the plan catalog and knows nothing about what a customer already holds;
 * teaching it would put a conditional into a component the create card also renders — a
 * change to shipped, money-moving code to save duplication on the riskier card. Not worth it.
 */
type ChangeAddOnRow = { on: boolean; screens: string; tier: string };

/**
 * Add-on rows SEEDED from what the customer already has.
 *
 * A held add-on renders pre-ticked with its current quantity, so an operator raising screens
 * does not have to restate add-ons they are not changing — and, more importantly, so an
 * untouched row sends the SAME absolute quantity back and is a no-op rather than a silent
 * reduction to zero.
 */
function ChangeAddOnSection({
  catalog,
  held,
  state,
  planAlias,
  currency,
  onChange,
  keyId,
  enabled,
  onToggle,
}: {
  catalog: PlanCatalogAddOn[];
  held: Record<string, { quantity: number; tier?: string | null }>;
  state: Record<string, ChangeAddOnRow>;
  planAlias: string;
  currency: string;
  onChange: (key: string, patch: Partial<ChangeAddOnRow>) => void;
  keyId: string;
  /** Closed by default, like the onboarding card's sections. */
  enabled: boolean;
  onToggle: (on: boolean) => void;
}) {
  if (!catalog.length) return null;
  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-dashed p-3"
      data-testid={`billing-${keyId}-addons`}
    >
      <div className="flex flex-col gap-0.5">
        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={enabled}
            onCheckedChange={(v) => onToggle(v === true)}
            className="cursor-pointer"
            data-testid={`billing-${keyId}-addons-enable`}
          />
          <span className="text-xs font-medium text-foreground">{'Add-ons (optional)'}</span>
        </label>
        {/*
          Says the thing the fields cannot: an add-on's number is the NEW TOTAL, whereas the
          screens field above is a delta. Two numeric fields on one card that read in
          opposite directions is a real trap, and "currently 3" next to an empty box does not
          resolve it.
        */}
        {enabled ? (
          <span className="text-[11px] text-muted-foreground">
            {'Enter how many screens to ADD to each add-on — the same as the screens field above. Leave a number blank to change nothing.'}
          </span>
        ) : null}
      </div>
      {enabled && catalog.map((a) => {
        const row = state[a.key] ?? { on: false, screens: '', tier: '' };
        const isHeld = !!held[a.key];
        const planOk = !a.requiresPlan.length || a.requiresPlan.includes(planAlias);
        const inPlan = !a.plans.length || a.plans.includes(planAlias);
        const priced = a.prices.some((p) => p.currency === currency);
        return (
          <div key={a.key} className="flex flex-col gap-1">
            <label className="flex cursor-pointer items-center gap-2 text-xs">
              <Checkbox
                checked={row.on}
                disabled={!planOk || !inPlan || !priced}
                // Same default as the create card: Standard, not an empty required field.
                onCheckedChange={(v) =>
                  onChange(a.key, {
                    on: v === true,
                    ...(v === true && a.tiers.length && !row.tier ? { tier: 'standard' } : {}),
                  })
                }
                className="cursor-pointer"
                data-testid={`billing-${keyId}-addon-${a.key}-toggle`}
              />
              <span>{a.label}</span>
              {isHeld ? (
                <span
                  className="text-muted-foreground"
                  data-testid={`billing-${keyId}-addon-${a.key}-held`}
                >
                  {`· currently ${held[a.key].quantity}`}
                </span>
              ) : null}
            </label>
            {row.on ? (
              <div className="flex flex-col gap-1 pl-6">
              <div className="flex items-center gap-2">
                {a.quantified ? (
                  <Input
                    value={row.screens}
                    // Same input rule as the screens field: digits with an optional leading
                    // minus, so an add-on can be reduced as well as raised.
                    onChange={(e) =>
                      onChange(a.key, {
                        screens: e.target.value.replace(/[^0-9-]/g, '').replace(/(?!^)-/g, ''),
                      })
                    }
                    placeholder="Screens to add"
                    // w-40, not w-24: the placeholder is the only thing telling the operator
                    // this box takes an amount to ADD rather than a total, and at w-24 it
                    // clipped to "Screens to a…". A field whose own label is unreadable is
                    // where the delta-vs-total mistake gets made. The create card's add-on
                    // input matches this width for the same reason.
                    className="w-40"
                    aria-label={`${a.label} screens to add`}
                    data-testid={`billing-${keyId}-addon-${a.key}-screens`}
                  />
                ) : null}
                {a.tiers.length ? (
                  <Select value={row.tier} onValueChange={(v) => onChange(a.key, { tier: v })}>
                    <SelectTrigger
                      className="w-32 cursor-pointer"
                      aria-label={`${a.label} tier`}
                      data-testid={`billing-${keyId}-addon-${a.key}-tier`}
                    >
                      <SelectValue placeholder="Tier" />
                    </SelectTrigger>
                    <SelectContent>
                      {a.tiers.map((t) => (
                        <SelectItem key={t} value={t} className="cursor-pointer">
                          {t === 'pro' ? 'Pro' : 'Standard'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
              </div>
              {/*
                The same "New total" phrasing as the screens field, so both numbers on this
                card are read the same way once the operator has learned one of them.
                `unchanged` is called out explicitly: a seeded row the operator did not touch
                is a no-op, and silence there looks identical to a change.
              */}
              {a.quantified ? (
                (() => {
                  const raw = (row.screens ?? '').trim();
                  // Not a usable number yet — say nothing rather than guess.
                  if (raw && !/^-?\d+$/.test(raw)) return null;
                  const move = raw ? parseInt(raw, 10) : 0;
                  const was = held[a.key]?.quantity ?? 0;
                  const total = was + move;
                  const hasHeld = !!held[a.key];

                  // Identical sentence to the screens field, so every number on this card
                  // reads the same way: what you type is what is ADDED, and the total is
                  // named outright because that is what the customer ends up paying for.
                  let label: string;
                  if (move === 0) {
                    label = hasHeld
                      ? `Unchanged — stays at ${was} screens on ${a.label}`
                      : `Enter how many screens of ${a.label} to add`;
                  } else if (total < 1) {
                    label = `${a.label} cannot go below 1 screen — this card does not remove an add-on`;
                  } else if (hasHeld) {
                    label = `New total: ${total} screens on ${a.label} (${was} ${
                      move < 0 ? '−' : '+'
                    } ${Math.abs(move)})`;
                  } else {
                    label = `New add-on: ${total} screens on ${a.label}`;
                  }
                  return (
                    <span
                      className={
                        total < 1 && move !== 0
                          ? 'text-[11px] text-destructive'
                          : 'text-[11px] text-muted-foreground'
                      }
                      data-testid={`billing-${keyId}-addon-${a.key}-total`}
                    >
                      {label}
                    </span>
                  );
                })()
              ) : null}
              </div>
            ) : null}
            {/* Each refusal names its own reason — "greyed out" with no explanation is the
                thing that generates support questions about a support tool. */}
            {!planOk ? (
              <span
                className="pl-6 text-xs text-muted-foreground"
                data-testid={`billing-${keyId}-addon-${a.key}-planError`}
              >
                {`Requires the ${a.requiresPlan.join(' or ')} plan`}
              </span>
            ) : !inPlan ? (
              <span
                className="pl-6 text-xs text-muted-foreground"
                data-testid={`billing-${keyId}-addon-${a.key}-catalogError`}
              >
                {'Not offered on this plan'}
              </span>
            ) : !priced ? (
              <span
                className="pl-6 text-xs text-muted-foreground"
                data-testid={`billing-${keyId}-addon-${a.key}-currencyError`}
              >
                {`No ${currency.toUpperCase()} price`}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Billing Console → "PO / Invoice - Change Screens / Add-ons".
 *
 * The sibling of Create Stripe Subscription, and the tool THAT card's guard defers to: it
 * refuses an account that already has a subscription, and this one refuses an account that
 * does not.
 *
 * The operator types a CUSTOMER id and nothing else identifying — the server resolves the
 * activated subscription itself, so there is no `sub_…` or `si_…` to mistype. Screens are
 * entered as a DELTA (what to add) while add-on quantities are ABSOLUTE, which is why the
 * card shows the resulting total prominently: the delta alone is not the number that lands.
 */
function ChangeSubscriptionForm({
  stripeId,
  customer,
}: {
  /** The console's shared customer. This card no longer owns an id field. */
  stripeId: string;
  customer: StripeCustomerBilling | null;
}) {
  const KEY = 'change-subscription';
  const empty = { delta: '', comment: '', billingEmail: '' };
  const [f, setF] = useState({ ...empty });
  const [addOns, setAddOns] = useState<Record<string, ChangeAddOnRow>>({});
  /**
   * Closed by default, matching the onboarding card. Unticking CLEARS the rows rather than
   * hiding them — on THIS card that matters more, not less: the numbers here change a live
   * subscription, so a hidden row that still submitted would alter what a customer is billed.
   */
  const [addOnsEnabled, setAddOnsEnabled] = useState(false);
  const toggleAddOns = (on: boolean) => {
    setAddOnsEnabled(on);
    if (!on) setAddOns({});
  };
  /** Same gate, same clear-on-untick reasoning, for hardware bought alongside the change. */
  const [devicesEnabled, setDevicesEnabled] = useState(false);
  const [deviceRegion, setDeviceRegion] = useState<HardwareRegion>('us');
  const [snapshot, setSnapshot] = useState<SubscriptionSnapshot | null>(null);
  const [proration, setProration] = useState<'IMMEDIATELY' | 'NEXT_INVOICE'>('IMMEDIATELY');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [result, setResult] = useState<SubscriptionChangeResult | null>(null);
  const [attachedDocId, setAttachedDocId] = useState<string | null>(null);
  /** Whether that document ended up cross-referenced — surfaced, not swallowed. */
  const [docLink, setDocLink] = useState<DocLinkState>({ kind: 'none' });
  /** Signature of the change last APPLIED, so an identical one cannot be submitted twice. */
  const [appliedSig, setAppliedSig] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const { data: catalogData } = useBillingPlanCatalog();
  const [loadSnapshot, { loading: snapLoading }] = useBillingSubscriptionSnapshot();
  const [changeSubscription] = useBillingChangeSubscription();

  /**
   * Load the baseline on blur of the customer id.
   *
   * On blur rather than per keystroke (one round trip per call, and a half-typed id is never
   * a real customer), and it SEEDS the add-on rows from what the customer holds. A failure is
   * surfaced here rather than left to submit, unlike the create card's address prefill: here
   * the snapshot is not a convenience, it is the number the delta is applied to.
   */
  /**
   * Which customer id the currently-displayed baseline belongs to.
   *
   * A ref rather than state because it gates an effect that also depends on it — as state it
   * would re-run the effect it just settled. It is what stops the debounce and the blur
   * handler both firing a read for the same id.
   */
  const loadedIdRef = useRef<string | null>(null);

  const loadBaseline = async (rawId?: string) => {
    const id = (rawId ?? stripeId).trim();
    if (!/^cus_\w{6,}$/i.test(id)) return;
    if (loadedIdRef.current === id) return;
    loadedIdRef.current = id;
    setResult(null);
    try {
      const r = await loadSnapshot({ variables: { stripeId: id } });
      const snap = r.data?.staffBillingSubscriptionSnapshot ?? null;
      setSnapshot(snap);
      if (snap?.blockedReason) {
        setMsg({ kind: 'err', text: snap.blockedReason });
        setAddOns({});
        return;
      }
      setMsg(null);
      // Seed every held add-on ticked at its current quantity, so leaving a row alone
      // re-sends the same absolute number instead of reading as a change.
      // Held add-ons come back TICKED but with an EMPTY number: the field means "how many
      // to add", so empty is "leave this one alone". Seeding the current total here would
      // read as adding it a second time.
      const seeded: Record<string, ChangeAddOnRow> = {};
      for (const a of snap?.addOns ?? []) {
        // `?? 'standard'` is not cosmetic. Standard is persisted as `planFeature: null`, so the
        // snapshot reports NO tier for a customer on Standard — seeding '' left the row ticked
        // with no tier, which `addOnsValid` rejects, so submit was silently disabled for every
        // customer already holding a tiered add-on at Standard. Harmless for untiered add-ons:
        // the payload only sends a tier when the catalog row has tiers.
        seeded[a.key] = { on: true, screens: '', tier: a.tier ?? 'standard' };
      }
      setAddOns(seeded);
    } catch (err) {
      // Release the id so editing it (or blurring again) retries rather than sitting on a
      // failure the operator cannot clear.
      loadedIdRef.current = null;
      setSnapshot(null);
      setMsg({ kind: 'err', text: (err as Error)?.message ?? 'Could not read this customer' });
    }
  };

  /**
   * Load as the id is typed, rather than making the operator click away to trigger it.
   *
   * Debounced, because this is a network read per call and a half-typed `cus_` is never a real
   * customer. 400ms is chosen to be past the end of a paste or a burst of typing without
   * feeling like a wait.
   *
   * The FIRST half of this effect is the safety-critical part, and it is not about loading at
   * all: the moment the id stops matching the loaded baseline, that baseline is thrown away.
   * The delta is applied to a specific customer's quantity, so showing customer A's screen
   * count while the field names customer B — and computing "+5" against A — is the one state
   * this card must never be in. Collapsing the form is the correct response, not a cosmetic one.
   */
  useEffect(() => {
    const id = stripeId.trim();
    if (loadedIdRef.current && loadedIdRef.current !== id) {
      loadedIdRef.current = null;
      setSnapshot(null);
      setAddOns({});
      setResult(null);
      setMsg(null);
    }
    if (!/^cus_\w{6,}$/i.test(id) || loadedIdRef.current === id) return;
    const timer = setTimeout(() => {
      void loadBaseline(id);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeId]);

  /**
   * Seed Billing Email from what Stripe holds, keyed on the customer rather than the snapshot:
   * it is a property of the CUSTOMER, not of the subscription this card changes.
   *
   * Prefilled for the same reason as the onboarding card's — the operator sees the address
   * Stripe already invoices before replacing it, so a good one cannot be clobbered unseen.
   */
  useEffect(() => {
    setF((s) => ({ ...s, billingEmail: customer?.billingEmail ?? '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.stripeId, customer?.billingEmail]);

  const catalog = catalogData?.staffBillingPlanCatalog;
  const catalogAddOns = catalog?.addOns ?? [];
  const planAlias = snapshot?.planAlias ?? '';
  const currency = snapshot?.currency ?? '';
  const held = Object.fromEntries(
    (snapshot?.addOns ?? []).map((a) => [a.key, { quantity: a.quantity, tier: a.tier }]),
  );

  // A leading '-' is allowed: decreases are permitted, and Stripe issues a CREDIT for one.
  const deltaRaw = f.delta.trim();
  const delta = /^-?\d+$/.test(deltaRaw) ? parseInt(deltaRaw, 10) : NaN;
  const current = snapshot?.stripeQuantity ?? null;
  const newTotal = current != null && Number.isFinite(delta) ? current + delta : null;
  /**
   * The same hook Create Hardware Only Invoice and the onboarding card use, so all three price
   * hardware identically. The currency is the SUBSCRIPTION's — not an operator choice here — so
   * there is no currency-change pruning to do on this card, only region.
   */
  const devices = useDeviceLines({ currency, region: deviceRegion });
  const toggleDevices = (on: boolean) => {
    setDevicesEnabled(on);
    if (!on) {
      devices.reset();
      setDeviceRegion('us');
    }
  };
  const changeDeviceRegion = (region: HardwareRegion) => {
    devices.pruneForRegion(region);
    setDeviceRegion(region);
  };
  const deviceRows = devicesEnabled ? devices.filledRows : [];
  const deviceQuantity = deviceRows.reduce((sum, r) => sum + (parseInt(r.quantity, 10) || 0), 0);
  /**
   * The cap measures the screens this change ADDS, not the resulting total — the same number
   * the server compares against. Requested 2026-08-23: hardware on this card is bought for the
   * screens being added, so N devices need N new licences, and an existing licence is assumed
   * to be carrying an existing device.
   *
   * A decrease adds nothing, so it clamps to 0 and ANY device then trips the cap. Since
   * 2026-09-25 the cap WARNS rather than blocks, so what it used to refuse — re-kitting screens
   * the customer KEEPS while downsizing, spares, replacements riding an add-on change — goes
   * through on the same change, with the operator told. (A fully flat change is still refused,
   * but by `devicesBlocked` below — the server rejects a no-op — not by the cap.)
   *
   * Fires only once the delta parses; a cap cannot be judged against an unknown number. It does
   * NOT need the snapshot — unlike the resulting total, the screens added are known from the
   * typed delta alone.
   */
  const screensAdded = Number.isFinite(delta) ? Math.max(delta, 0) : null;
  const overScreenLimit = screensAdded != null && deviceQuantity > screensAdded;
  const devicesOk = deviceRows.length === 0 || devices.linesValid;

  const comment = f.comment.trim();
  const overLimit = comment.length > INVOICE_COMMENT_MAX;

  const ticked = catalogAddOns.filter((a) => addOns[a.key]?.on);

  /**
   * Every add-on number on this card is a DELTA — "how many to add" — exactly like the
   * screens field. The server's contract is ABSOLUTE (`maxQuantity`), so the delta is
   * converted at submit against what the customer currently holds.
   *
   * Returns null when the row's number is not usable yet.
   */
  const addOnDelta = (key: string): number | null => {
    const raw = (addOns[key]?.screens ?? '').trim();
    if (!raw) return 0; // empty means "leave this one alone"
    return /^-?\d+$/.test(raw) ? parseInt(raw, 10) : null;
  };
  const addOnTotal = (key: string): number | null => {
    const d = addOnDelta(key);
    if (d === null) return null;
    return (held[key]?.quantity ?? 0) + d;
  };

  const addOnsValid = ticked.every((a) => {
    const row = addOns[a.key];
    if (a.tiers.length && !row.tier) return false;
    if (a.requiresPlan.length && !a.requiresPlan.includes(planAlias)) return false;
    if (a.plans.length && !a.plans.includes(planAlias)) return false;
    if (!a.prices.some((p) => p.currency === currency)) return false;
    if (a.quantified) {
      const total = addOnTotal(a.key);
      if (total === null) return false;
      // An add-on has to end up licensed for at least one screen. Reaching zero is a
      // REMOVAL, which this card does not do — it would need Stripe's `deleted: true`, and
      // the entitlement write refuses a zero quantity anyway.
      if (total < 1) return false;
      // A brand-new add-on with nothing added is not a selection, it is an empty row.
      if (!held[a.key] && total < 1) return false;
    }
    return true;
  });

  /**
   * Is anything actually changing? An untouched card whose add-on rows were seeded from the
   * customer's current state would otherwise look valid while changing nothing — the server
   * refuses that, but the button should not invite it.
   */
  const addOnsChanged = ticked.some((a) => {
    const row = addOns[a.key];
    const h = held[a.key];
    if (!h) return true; // ticking an add-on the customer does not hold is always a change
    if (a.quantified && (addOnDelta(a.key) ?? 0) !== 0) return true;
    if (a.tiers.length && (row.tier || '') !== (h.tier || '')) return true;
    return false;
  });
  const changesSomething = (Number.isFinite(delta) && delta !== 0) || addOnsChanged;

  /**
   * Devices have no change to ride yet, so no hardware can be entered AT ALL — the picker is
   * replaced by the notice rather than merely accompanied by one.
   *
   * Two states, one condition, because they are the same predicament to the operator:
   *   - the screens field is blank or half-typed (`delta` is NaN). Submit is gated on
   *     `Number.isFinite(delta)`, so this blocks the card even when an add-on IS changing —
   *     and, worse, `screensAdded` is null, so the device CAP cannot be computed and an
   *     over-order would go through without its warning.
   *   - nothing is actually changing (0 screens, no add-on moved). Devices ride a subscription
   *     change on this card; they are not one themselves, and the server refuses a no-op.
   *
   * Withholding the picker is the point: a priced, pickable product list with a running
   * subtotal reads as an order being built, and every one of those lines is unbillable. The
   * cap is the sharper reason — it is the guard that cannot even run in this state, so letting
   * lines be entered means letting them be entered UNCHECKED.
   *
   * The checkbox itself stays operable on purpose. Disabling it would trap an operator who
   * ticked hardware, picked lines, then cleared the screens field: the section would be locked
   * open with no way to close it. The lines survive in state meanwhile, so re-entering a screen
   * change brings them back rather than making the operator pick them again.
   */
  const devicesBlocked = !Number.isFinite(delta) || !changesSomething;

  /**
   * Everything below the customer id stays hidden until a subscription actually resolved.
   *
   * Not merely to save space: every field down there is *about* the resolved subscription —
   * the add-on rows are seeded from what the customer holds, the proration choice decides
   * whether THEIR invoice is raised now, and the document is filed against THEIR account. A
   * blank add-on list rendered before we know the customer is not an empty state, it is a
   * wrong one, and it invites filling in a form that will be re-seeded underneath you.
   *
   * Deliberately keyed on `subscriptionId` rather than on the id field being non-empty: a
   * typed-but-unresolved id, or one that came back blocked, must NOT open the form.
   */
  const ready = !!snapshot && !snapshot.blockedReason && !!snapshot.subscriptionId;

  /**
   * D17a — this customer charges a card, so the change is wrapped in a temporary flip to
   * invoice terms: the proration is emailed to be paid manually, then the subscription goes
   * straight back on automatic charge so renewals are unaffected.
   *
   * Two things follow, and the server enforces both — this is the courtesy half:
   *   - a warning above submit, because the operator is about to change HOW this customer
   *     is billed for this one invoice and should not discover that from the result;
   *   - the proration choice is locked to IMMEDIATELY. On NEXT_INVOICE Stripe raises no
   *     invoice now and sweeps the proration onto the next renewal — which by then is back
   *     on automatic charge, so it would CHARGE THE CARD weeks later. That is the outcome
   *     the flip exists to prevent, so it is not offered.
   */
  const flipping = ready && !!snapshot?.collectionFlipRequired;

  // The select stops OFFERING NEXT_INVOICE while flipping, but a value already in state
  // survives — pick NEXT_INVOICE for one customer, then load a card-on-file one into the
  // shared customer field and the select would render blank over a value the server refuses.
  // Forced here so what is displayed, what is submitted and what the server accepts agree.
  useEffect(() => {
    if (flipping) setProration('IMMEDIATELY');
  }, [flipping]);

  /**
   * What this submit would APPLY, as one comparable value.
   *
   * The screens field is deliberately NOT cleared after a successful change (requested
   * 2026-08-23: the operator wants to still see what they just did). `valid` does not look at
   * `result`, so without this guard the same +2 could be clicked a second time and raise a
   * second prorated invoice against a customer — the clear was what prevented that.
   *
   * The comment is excluded on purpose: rewording the PO note does not make it a different
   * change, so it must not re-arm the button. Changing a screen count, proration, an add-on
   * quantity or a device line does.
   */
  const changeSig = JSON.stringify({
    delta: Number.isFinite(delta) ? delta : null,
    proration,
    addOns: ticked.map((a) => [a.key, addOnTotal(a.key), addOns[a.key]?.tier ?? null]),
    devices: deviceRows.map((r) => [r.optionKey, r.quantity]),
    region: deviceRows.length ? deviceRegion : null,
  });
  const alreadyApplied = appliedSig != null && appliedSig === changeSig;

  /**
   * Billing email — same rules as the onboarding card, deliberately identical so an operator
   * meets ONE behaviour across the console: prefilled, sent only when changed, trimmed on both
   * sides so a stray space is not an edit, and blank never sent (Stripe reads '' as "clear the
   * email", which would leave the customer with no invoice recipient).
   *
   * NOT part of `changeSig`: it is a customer property, not part of what this change applies,
   * so editing it must not re-arm a duplicate subscription change.
   */
  const billingEmail = f.billingEmail.trim();
  const billingEmailDirty = billingEmail !== (customer?.billingEmail ?? '').trim();
  const billingEmailUsable = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail);
  const billingEmailBad = billingEmailDirty && !billingEmailUsable;

  const valid =
    !!snapshot &&
    !snapshot.blockedReason &&
    !!snapshot.subscriptionId &&
    Number.isFinite(delta) &&
    newTotal != null &&
    newTotal >= 1 &&
    changesSomething &&
    comment.length > 0 &&
    !overLimit &&
    addOnsValid &&
    devicesOk &&
    // A malformed EDIT blocks; leaving the prefill alone never does.
    !billingEmailBad &&
    !alreadyApplied;

  const clear = () => {
    // The shared customer is NOT cleared: an operator usually does more than one thing to
    // one account, and clearing it here would undo the point of entering it once.
    setF({ ...empty });
    setAddOns({});
    setAddOnsEnabled(false);
    setDevicesEnabled(false);
    setDeviceRegion('us');
    devices.reset();
    loadedIdRef.current = null;
    setSnapshot(null);
    setProration('IMMEDIATELY');
    setMsg(null);
    setResult(null);
    setAttachedDocId(null);
    setDocLink({ kind: 'none' });
    setAppliedSig(null);
    setF((s) => ({ ...s, billingEmail: customer?.billingEmail ?? '' }));
  };

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    setResult(null);
    try {
      const payload = {
        stripeId: stripeId.trim(),
        screensDelta: delta,
        proration,
        // Sent ONLY when the operator changed it — an untouched prefill costs no write.
        ...(billingEmailDirty && billingEmailUsable ? { billingEmail } : {}),
        comment,
        ...(ticked.length
          ? {
              addOns: ticked.map((a) => ({
                key: a.key,
                // The server wants the ABSOLUTE quantity; the operator typed a delta.
                ...(a.quantified ? { maxQuantity: addOnTotal(a.key) as number } : {}),
                ...(a.tiers.length ? { tier: addOns[a.key].tier } : {}),
              })),
            }
          : {}),
        // Read off the hook's filled rows, so an unticked section cannot submit hardware it is
        // no longer showing.
        ...(deviceRows.length ? { deviceLines: devices.payloadLines(), deviceRegion } : {}),
        // See the hardware card. The server puts it on whichever invoice this change raises
        // — the proration one, or the standalone device draft when there is no proration.
        ...(attachedDocId ? { documentId: attachedDocId } : {}),
      };
      const res = await changeSubscription({ variables: { input: payload } });
      const r = res.data?.staffBillingChangeSubscription;
      if (!r) throw new Error('No result returned');
      setResult(r);
      // Only linkable when an invoice actually exists — the next-invoice path has none.
      // Which invoice the document belongs to depends on what this change actually raised.
      //
      // On the NEXT_INVOICE path the proration rides the next renewal, so there is no proration
      // invoice — but hardware does NOT wait for it, it gets its OWN invoice raised now. That
      // invoice is the only one that exists, so it is the one the PO should point at. Reporting
      // "no invoice" while a real hardware invoice had just been created was simply wrong.
      //
      // Chosen as a PAIR, never `id ?? id` with `url ?? url`: mixing one invoice's id with the
      // other's URL would produce a cross-reference that opens the wrong document.
      const docTarget = r.prorationInvoiceId
        ? { id: r.prorationInvoiceId, url: r.prorationInvoiceUrl }
        : { id: r.deviceInvoiceId, url: r.deviceInvoiceUrl };
      setDocLink(
        await linkInvoiceToDocument(attachedDocId, docTarget.id, docTarget.url, delta),
      );
      setMsg({ kind: r.mongoUpdated ? 'ok' : 'err', text: r.message });
      if (r.mongoUpdated) {
        // Re-read rather than patch local state: the authoritative new baseline is whatever
        // Stripe now bills, and a stale `current` would make the NEXT delta land wrong.
        const again = await loadSnapshot({ variables: { stripeId: stripeId.trim() } });
        setSnapshot(again.data?.staffBillingSubscriptionSnapshot ?? null);
        // The delta is KEPT on screen on purpose — the operator wants to see what they just
        // applied against the new baseline. Recording the signature is what stops it being
        // applied again; clearing the field used to do that job.
        setAppliedSig(changeSig);
      }
    } catch (err) {
      setMsg({ kind: 'err', text: (err as Error)?.message ?? 'Change failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <CardShell
      // `billing-card-<key>` for the shell, `billing-<key>-<field>` for the fields — the
      // convention every other card here follows, and what the e2e card-inventory asserts.
      testId={`billing-card-${KEY}`}
      title="PO / Invoice - Change Screens / Add-ons"
      description="Raise or lower screens and add or re-quantify add-ons on an existing subscription. Enter the customer's Stripe ID — the subscription is found for you."
    >

      {ready ? (
        <>
        <div className="grid gap-3 sm:grid-cols-2">
        <LabeledInput
          id={`${KEY}-delta`}
          label="Screens to add"
          value={f.delta}
          onChange={set('delta')}
          placeholder="e.g. 15, or -5"
          // No "enter a Stripe ID" fallback: this field only exists once the subscription
          // has resolved, so there is always a baseline to add to.
          helper={
            // Once applied, the snapshot has been re-read, so `current` IS the new total —
            // rendering "new total" off the retained delta would describe a second change
            // that has not happened.
            alreadyApplied
              ? `Applied — the account is now on ${current} screens. Edit a field, or press Clear, to make another change.`
              : newTotal != null && Number.isFinite(delta)
                ? `New total: ${newTotal} screens on the account (${current} ${
                    delta < 0 ? '−' : '+'
                  } ${Math.abs(delta)})`
                : 'Enter how many screens to add'
          }
          testId={`billing-${KEY}-delta`}
        />
        {/* Second cell of the row, so it sits to the RIGHT of Screens to add. Same field,
            same rules and same copy as the onboarding card — one behaviour to learn. */}
        <LabeledInput
          id={`${KEY}-billingEmail`}
          label="Billing Email"
          value={f.billingEmail}
          onChange={set('billingEmail')}
          placeholder="billing@customer.com"
          helper={
            billingEmailBad
              ? 'That is not a usable email — Stripe sends every invoice for this customer to it.'
              : billingEmailDirty
                ? 'Will be SAVED to the Stripe customer — this redirects every future invoice for them, not just this one.'
                : customer?.billingEmail
                  ? 'From Stripe. Edit to change where this customer’s invoices are sent.'
                  : 'Stripe holds no billing email for this customer — invoices have nowhere to go until one is set.'
          }
          testId={`billing-${KEY}-billingEmail`}
        />
        </div>

        {/*
          The baseline strip now sits BELOW the row rather than between the two fields: it
          describes the customer both fields refer to, and putting it mid-row would separate
          the delta from its own label. Everything the delta depends on, stated before the
          operator commits to it.
        */}
        {snapshot && !snapshot.blockedReason ? (
          <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-baseline`}>
            {[
              // Identify the customer by something a human recognises. The accountId is a
              // last resort, not the default: it names the tenant but tells the operator
              // nothing, and plenty of accounts carry no `name` at all — which is exactly
              // when the owner's email is the only useful handle.
              snapshot.companyName,
              snapshot.ownerEmail,
              !snapshot.companyName && !snapshot.ownerEmail ? snapshot.accountId : null,
              snapshot.planAlias,
              (snapshot.currency ?? '').toUpperCase(),
              `${snapshot.mongoQuantity ?? snapshot.stripeQuantity ?? '?'} screens`,
              snapshot.status,
              snapshot.collectionLabel,
            ]
              .filter(Boolean)
              .join(' · ')}
            {snapshot.quantityDrift
              ? ` · ⚠ Stripe bills ${snapshot.stripeQuantity} but the account records ${snapshot.mongoQuantity} — this change corrects it`
              : ''}
          </p>
        ) : null}
        {/*
          D17a. Its own paragraph rather than another ` · ` clause on the baseline strip: the
          strip DESCRIBES the customer, and this says what submitting will DO to them. Amber
          for the same reason the credit warning below is — it is not an error, the change is
          going to work, but the billing behaves differently from every other run of this card
          and the operator should not learn that from the result.
        */}
        {flipping ? (
          <p className="text-xs text-amber-600" data-testid={`billing-${KEY}-collection-flip`}>
            {'This customer is on “Automatically charge a payment method on file”. This change ' +
              'will be billed as an invoice EMAILED to them to pay manually — Stripe sends it about ' +
              'an hour after you submit. The subscription goes straight back on automatic charge, ' +
              'so renewals keep charging their card as before, and the card on file is not touched.'}
          </p>
        ) : null}
        {newTotal != null && Number.isFinite(delta) && delta < 0 ? (
          <p className="text-xs text-amber-600" data-testid={`billing-${KEY}-credit-warning`}>
            {'Reducing screens issues a CREDIT, not a charge. It does not remove devices the customer has already registered.'}
          </p>
        ) : null}
        <ChangeAddOnSection
          catalog={catalogAddOns}
          held={held}
          state={addOns}
          planAlias={planAlias}
          currency={currency}
          onChange={(key, patch) =>
            setAddOns((s) => ({
              ...s,
              [key]: { ...(s[key] ?? { on: false, screens: '', tier: '' }), ...patch },
            }))
          }
          keyId={KEY}
          enabled={addOnsEnabled}
          onToggle={toggleAddOns}
        />

        <LabeledSelect
          id={`${KEY}-proration`}
          label="Proration"
          value={proration}
          onChange={(v) => setProration(v as 'IMMEDIATELY' | 'NEXT_INVOICE')}
          // Never actually rendered: SelectValue shows a placeholder only when the value is
          // empty, and proration always carries one (IMMEDIATELY by default, matching the
          // GraphQL input's own default). Supplied because the prop is required.
          placeholder="Immediately after update"
          // NEXT_INVOICE is withheld, not disabled, when flipping: an option that cannot be
          // chosen is noise, and the helper below already says why there is nothing to choose.
          options={
            flipping
              ? [{ value: 'IMMEDIATELY', label: 'Immediately after update' }]
              : [
                  { value: 'IMMEDIATELY', label: 'Immediately after update' },
                  { value: 'NEXT_INVOICE', label: 'On next invoice after update' },
                ]
          }
          helper={
            flipping
              ? 'Locked: this customer is on automatic charge, so the proration has to be invoiced now — held to the next renewal it would be charged to their card instead of emailed.'
              : proration === 'IMMEDIATELY'
                ? 'Raises a prorated invoice now and emails it to the customer to pay.'
                : 'No invoice is raised now — the proration rides the next renewal invoice, and the memo must be added by hand when it drafts.'
          }
          testId={`billing-${KEY}-proration`}
        />

        {/* Hardware bought alongside the change. Placed AFTER the proration select on purpose:
            the proration mode decides which invoice these lines land on, so the operator has
            already made that choice by the time they read this section's copy. Same dashed
            container and checkbox gate as Add-ons above, and the same picker + pricing rules as
            Create Hardware Only Invoice, because it is the same hook. */}
        <div
          className="flex flex-col gap-2 rounded-md border border-dashed p-3"
          data-testid={`billing-${KEY}-devices`}
        >
          <div className="flex flex-col gap-0.5">
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={devicesEnabled}
                onCheckedChange={(v) => toggleDevices(v === true)}
                className="cursor-pointer"
                data-testid={`billing-${KEY}-devices-enable`}
              />
              <span className="text-xs font-medium text-foreground">{'Hardware (optional)'}</span>
            </label>
            {/* WHERE the hardware is billed depends on the proration mode, and the two answers
                are genuinely different documents — so the copy follows the select rather than
                describing one case and leaving the operator to guess the other. */}
            <p className="text-[11px] text-muted-foreground">
              {proration === 'IMMEDIATELY'
                ? 'Added to the SAME prorated invoice as this change, so the customer receives one bill. Priced live from the device catalog. Hardware is never discountable, so a coupon on this account reduces the subscription only.'
                : 'Billed on their OWN invoice, raised now — the proration waits for the next renewal, but hardware being shipped does not wait with it. Priced live from the device catalog, and never discountable.'}
            </p>
          </div>
          {devicesEnabled && devicesBlocked ? (
            <p
              className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
              data-testid={`billing-${KEY}-devices-need-plan`}
            >
              {
                'Buying devices without a subscription? Use Create Hardware Only Invoice instead — this card bills devices on a NEW subscription\u2019s first invoice, so it needs a plan.'
              }
            </p>
          ) : null}
          {devicesEnabled && !devicesBlocked ? (
            <>
              <div className="w-[220px]">
                <LabeledSelect
                  id={`${KEY}-deviceRegion`}
                  label="Ship to"
                  value={deviceRegion}
                  onChange={(v: string) => changeDeviceRegion(v as HardwareRegion)}
                  placeholder="Select region"
                  options={SHIP_REGIONS}
                  testId={`billing-${KEY}-deviceRegion`}
                />
              </div>
              <DeviceLineRows
                keyId={KEY}
                rows={devices.rows}
                setRows={devices.setRows}
                options={devices.options}
                allProducts={devices.allProducts}
                currency={currency}
                catalogLoading={devices.catalogLoading}
                pruned={devices.pruned}
                disabled={busy}
                label=""
                formatAmount={formatAmount}
                LabeledSelect={LabeledSelect}
                LabeledInput={LabeledInput}
              />
              {devices.duplicateSku ? (
                <p
                  className="text-xs text-destructive"
                  data-testid={`billing-${KEY}-device-duplicate`}
                >
                  {'The same configuration is on two lines — combine them into one quantity.'}
                </p>
              ) : null}
              {/* The cap names the screens this change ADDS — the number it is judged against.
                  On a decrease that is 0, which is why the message can read "for 0 screens". */}
              {overScreenLimit ? (
                <p
                  className="text-xs text-warning"
                  data-testid={`billing-${KEY}-device-over-limit`}
                >
                  {`${deviceQuantity} device${deviceQuantity === 1 ? '' : 's'} for ${screensAdded} screen${
                    screensAdded === 1 ? '' : 's'
                  } — a device without a screen licence cannot be activated. You can still submit if that is intended (spares, replacements).`}
                </p>
              ) : null}
              {devices.filledRows.length > 0 ? (
                <p
                  className="text-xs text-muted-foreground"
                  data-testid={`billing-${KEY}-device-subtotal`}
                >
                  {`Hardware subtotal ${formatAmount(devices.subtotal, currency)} — shipping and tax are added by the server.`}
                </p>
              ) : null}
            </>
          ) : null}
        </div>

        <LabeledInput
          id={`${KEY}-comment`}
          label="Comment"
          value={f.comment}
          onChange={set('comment')}
          placeholder="PO reference / why this changed"
          helper={
            proration === 'IMMEDIATELY'
              ? "Becomes the invoice memo — the customer sees it. Max 500 characters."
              : 'Recorded on the subscription and the audit log. Max 500 characters.'
          }
          testId={`billing-${KEY}-comment`}
        />
        {overLimit ? (
          <p className="text-xs text-destructive" data-testid={`billing-${KEY}-commentError`}>
            {`Comment is ${comment.length} characters — the limit is ${INVOICE_COMMENT_MAX}.`}
          </p>
        ) : null}

        <DocumentAttachment stripeId={stripeId} keyId={KEY} onAttached={setAttachedDocId} />
        </>
      ) : (
        <p
          className="text-xs text-muted-foreground"
          data-testid={`billing-${KEY}-awaiting-customer`}
        >
          {'Enter a customer Stripe ID above to load their subscription — the add-ons, proration and comment appear once it resolves.'}
        </p>
      )}

      <ActionButtons
        keyId={KEY}
        submitLabel="Change Subscription"
        valid={valid}
        busy={busy}
        onSubmit={submit}
        onClear={clear}
      />
      <ResultLine msg={msg} testId={`billing-${KEY}-result`} />
      <DocLinkLine state={docLink} keyId={KEY} />
      {result ? <SubscriptionChangePanel result={result} /> : null}
    </CardShell>
  );
}

/**
 * What the change did. Three independent facts, reported independently because they fail
 * independently: the subscription changed, an invoice was (or was not) raised, and Mongo was
 * (or was not) updated.
 */
function SubscriptionChangePanel({ result }: { result: SubscriptionChangeResult }) {
  const KEY = 'change-subscription';
  const hasInvoice = !!result.prorationInvoiceId;
  return (
    <div className="flex flex-col gap-2" data-testid={`billing-${KEY}-outcome`}>
      {/*
        D17a. FIRST, above even the Mongo failure, and destructive rather than warning: every
        other banner here describes something that went wrong with THIS change, which an
        operator can finish by hand today. This one has been left on the customer — until
        somebody sets the collection method back, every RENEWAL is emailed instead of charged,
        and nothing in this console will mention it again.

        Strict === false, the same three-state contract as the schedule banners: `undefined`
        means no flip was needed (the customer was already invoiced) and must render nothing.
      */}
      {result.collectionRestored === false ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid={`billing-${KEY}-collection-restore-failed`}
        >
          {`⚠ This customer has been LEFT on “Email invoice to the customer to pay manually”. Set ${result.subscriptionId} back to “Automatically charge a payment method on file” in Stripe — until you do, every renewal is emailed instead of charged to their card.`}
        </p>
      ) : null}
      {!result.mongoUpdated ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid={`billing-${KEY}-mongo-failed`}
        >
          {`⚠ Stripe HAS been changed but the account was NOT updated. Do NOT submit again — that would apply the change a second time. Set the screen count with the "Only Mongo Updated - Update Screen Quantity" card using the figures below.`}
        </p>
      ) : null}
      {result.mongoUpdated && result.invoiceMemoSet === false ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${KEY}-memo-failed`}
        >
          {`⚠ Everything worked except the invoice memo — your comment is NOT on the invoice. Add it in Stripe (Edit invoice → Memo) before it finalizes.`}
        </p>
      ) : null}
      {/*
        Strict === false, same three-state contract as the create card. The COPY differs on
        purpose, and the difference is load-bearing: probed 2026-08-19, Stripe puts its OWN
        `auto_advance: true` and ~1h `automatically_finalizes_at` on a proration invoice
        unprompted. So an unscheduled proration invoice does NOT sit as a draft forever — it
        sends on Stripe's timing instead of ours. Reusing the create card's "the customer
        never receives it" wording here would be false and would send someone finalizing an
        invoice by hand that was already going out.
      */}
      {result.invoiceFinalizeScheduled === false ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${KEY}-finalize-warning`}
        >
          {`⚠ Our auto-send deadline was not applied — the invoice follows Stripe's own ~1 hour schedule instead. Review it now if you need longer.`}
        </p>
      ) : null}
      {result.invoiceFinalizesAt ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs font-medium text-warning"
          data-testid={`billing-${KEY}-finalizes-at`}
        >
          {`⏱ Auto-sends ${formatFinalizeDeadline(result.invoiceFinalizesAt)}${
            result.invoiceFinalizeRule === 'opticlaw' ? ' (raised by OptiClaw)' : ''
          } — review it in Stripe before then.`}
        </p>
      ) : null}
      {/*
        No invoice is a normal outcome on the next-invoice path, so it is stated rather than
        left as a gap where the operator expects a link.
      */}
      {!hasInvoice ? (
        <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-no-invoice`}>
          {result.prorationApplied === 'next_invoice'
            ? 'No invoice was raised now — the proration rides the next renewal invoice, so add the memo by hand when it drafts.'
            : 'Stripe raised no prorated invoice for this change. Check the subscription in Stripe if you expected a charge.'}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-summary`}>
        {[
          result.companyName ?? result.accountId,
          result.subscriptionId,
          `${result.previousScreens} → ${result.newScreens} screens`,
          result.prorationTotal != null
            ? formatAmount(result.prorationTotal, result.currency)
            : null,
          result.status,
        ]
          .filter(Boolean)
          .join(' — ')}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {hasInvoice && result.prorationInvoiceUrl ? (
          <a
            href={result.prorationInvoiceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-primary underline underline-offset-2"
            data-testid={`billing-${KEY}-invoice-link`}
          >
            {'Open the prorated invoice in Stripe →'}
          </a>
        ) : null}
        {/* Only when the hardware went somewhere ELSE. On the immediate path it is the same
            invoice the link above already opens, and two links to one document is noise. */}
        {result.deviceInvoiceUrl && result.deviceInvoiceId !== result.prorationInvoiceId ? (
          <a
            href={result.deviceInvoiceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-primary underline underline-offset-2"
            data-testid={`billing-${KEY}-device-invoice-link`}
          >
            {'Open the hardware invoice in Stripe →'}
          </a>
        ) : null}
        <a
          href={result.subscriptionUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-muted-foreground underline underline-offset-2"
          data-testid={`billing-${KEY}-subscription-link`}
        >
          {'Open the subscription →'}
        </a>
      </div>
      {/* Says WHICH invoice carries the hardware, in words. The `message` already covers it,
          but that string also carries any warning, and this is the line an operator scans for
          when they are checking their own work. */}
      {result.deviceItemsCreated > 0 ? (
        <p className="text-[11px] text-muted-foreground" data-testid={`billing-${KEY}-device-summary`}>
          {`${result.deviceItemsCreated} device/freight line${
            result.deviceItemsCreated === 1 ? '' : 's'
          }${
            result.deviceAmount != null
              ? ` (${formatAmount(result.deviceAmount, result.currency)})`
              : ''
          } on invoice ${result.deviceInvoiceId}${
            result.deviceInvoiceId === result.prorationInvoiceId
              ? ' — the same invoice as this change.'
              : ' — a separate hardware invoice.'
          }`}
        </p>
      ) : null}
      {result.driftHealed ? (
        <p className="text-[11px] text-muted-foreground" data-testid={`billing-${KEY}-drift-healed`}>
          {'The recorded Stripe ids disagreed with Stripe and were corrected on the account.'}
        </p>
      ) : null}
      {/* The item ids now on the subscription, echoed so they can be checked against Stripe —
          and so a hand-finish has them if the Mongo write failed. */}
      <ul className="flex flex-col gap-0.5" data-testid={`billing-${KEY}-lines`}>
        {result.lines.map((line) => (
          <li key={line.key} className="text-[11px] text-muted-foreground">
            {`${line.label} — ${line.subscriptionItemId}${
              line.key === 'plan' ? ' (subscription_normal_id)' : ''
            } × ${line.quantity}`}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The created subscription: what to click, what was created, and — when it happened —
 * the one failure that must not be mistaken for "nothing happened".
 */
function SubscriptionResult({ result }: { result: StripeSubscriptionResult }) {
  const KEY = 'create-stripe-subscription';
  return (
    <div className="flex flex-col gap-2" data-testid={`billing-${KEY}-outcome`}>
      {!result.mongoUpdated ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid={`billing-${KEY}-mongo-failed`}
        >
          {`⚠ The subscription EXISTS in Stripe but the account was NOT onboarded. Do NOT submit again — that would create a second subscription. Finish it with Normal Onboarding using the ids below.`}
        </p>
      ) : null}
      {result.mongoUpdated && !result.invoiceMemoSet ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${KEY}-memo-failed`}
        >
          {`⚠ Everything worked except the invoice memo — your comment is NOT on the invoice. Add it in Stripe (Edit invoice → Memo) before it finalizes.`}
        </p>
      ) : null}
      {/*
        Strict === false. `undefined` means no window was requested (the feature is off),
        which is not a failure and must render nothing — `!invoiceFinalizeScheduled`
        would warn in every environment where it is deliberately dark. This is a SIBLING
        of the memo banner, not part of its condition: the memo and the schedule fail
        independently, and an operator needs to know which one bit.
      */}
      {result.invoiceFinalizeScheduled === false ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${KEY}-finalize-warning`}
        >
          {`⚠ Auto-send was NOT scheduled — this invoice will sit as a draft until someone finalizes it in Stripe. Do it by hand, or the customer never receives it.`}
        </p>
      ) : null}
      {result.invoiceFinalizesAt ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs font-medium text-warning"
          data-testid={`billing-${KEY}-finalizes-at`}
        >
          {`⏱ Auto-sends ${formatFinalizeDeadline(result.invoiceFinalizesAt)}${
            result.invoiceFinalizeRule === 'opticlaw' ? ' (raised by OptiClaw)' : ''
          } — review it in Stripe before then.`}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-summary`}>
        {[
          result.companyName ?? result.accountId,
          result.subscriptionId,
          formatAmount(result.invoiceTotal, result.currency),
          result.invoiceStatus ? `invoice ${result.invoiceStatus}` : null,
        ]
          .filter(Boolean)
          .join(' — ')}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {result.invoiceUrl ? (
          <a
            href={result.invoiceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-primary underline underline-offset-2"
            data-testid={`billing-${KEY}-invoice-link`}
          >
            {result.invoiceStatus === 'draft'
              ? 'Open the draft invoice in Stripe →'
              : 'Open the invoice in Stripe →'}
          </a>
        ) : null}
        <a
          href={result.subscriptionUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-muted-foreground underline underline-offset-2"
          data-testid={`billing-${KEY}-subscription-link`}
        >
          {'Open the subscription →'}
        </a>
      </div>
      {/* The ids we persisted, echoed so the operator can check them against Stripe.
          They are also what a hand-finish needs if the Mongo write failed. */}
      <ul className="flex flex-col gap-0.5" data-testid={`billing-${KEY}-lines`}>
        {result.lines.map((line) => (
          <li key={line.key} className="text-[11px] text-muted-foreground">
            {`${line.label} — ${line.subscriptionItemId}${
              line.key === 'plan' ? ' (subscription_normal_id)' : ''
            }`}
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-muted-foreground">{result.message}</p>
    </div>
  );
}

// ─────────────────── Op 8: Apply Stripe Coupon ───────────────────
// The card TITLE is display copy and can change; the KEY ('apply-coupon'), its testids and the
// `apply_coupon` metadata value are identifiers and deliberately do not follow it — renaming
// those would churn e2e selectors and rewrite the meaning of data already in Stripe. The one
// thing that MUST track the title is the audit tool label in api op-display.ts, which cites it
// verbatim so an Activity Log row names a card the reader can actually open.
/**
 * Attaches one of the catalog's approved coupons to the Stripe CUSTOMER.
 *
 * Three properties of Stripe drive the whole shape of this card:
 *   - A customer holds exactly ONE discount, and attaching a second silently displaces
 *     the first with no history kept. So an existing discount REFUSES the apply and
 *     surfaces a Remove button instead — there is no "replace" here, deliberately.
 *   - `customers.update` has no compare-and-set, so the customer is read before (the
 *     preview the form is gated on) and read back after (the outcome panel's discount).
 *   - Hardware invoices are raised with `discounts: ''`, so a customer-level coupon
 *     never reaches them. Every scope sentence in this card says so.
 *
 * The client never names a coupon: it sends the catalog KEY and staff-api re-resolves the
 * Stripe id, the terms and the tax-exempt flag from its own row. Which is also why the
 * document gate mirrored below is advisory — the server re-checks all of it.
 */

/**
 * Fire the customer lookup only on a plausible id.
 *
 * Same shape the subscription card uses for its address prefill: a half-typed `cus_` is
 * never a real customer, and this is a network round trip per call.
 */
const CUS_LOOKUP_RE = /^cus_\w{6,}$/i;

/**
 * The two document statuses staff-api's gate accepts (its `deriveStatus`). Mirrored here
 * so the certificate picker only OFFERS what would be accepted — advisory only: the
 * server re-derives the status, re-checks `verified_at`, and re-reads the coupon's
 * tax-exempt flag from the catalog on every apply.
 */
const DOC_STATUS_ELIGIBLE = ['valid', 'expiring_soon'];

type DocMode = 'existing' | 'upload';

/**
 * Why a certificate cannot be used, or null when it can.
 *
 * `verified_at` is checked LAST and is what the server actually requires. Since 2026-08-12
 * an upload self-stamps it with the uploader, so in practice it fails only for documents
 * filed before that — hence the "verify it in the Document Center" wording, which is now a
 * button on the customer's Documents tab. Read the field as "a named staff member filed
 * this", NOT "a second person reviewed it": nothing inspects the bytes.
 */
/**
 * Display form for a picker row's `doc_type`. The field crosses GraphQL as a plain String, so
 * an unknown value falls back to itself rather than rendering `undefined` — a type added on the
 * server before this card is redeployed must still read as something.
 */
function docTypeLabel(docType: string): string {
  return DOC_TYPE_LABEL[docType as DocType] ?? docType;
}

function docIneligibleReason(doc: BillingTaxExemptDocument): string | null {
  if (!DOC_STATUS_ELIGIBLE.includes(doc.status)) {
    switch (doc.status) {
      case 'archived':
        return 'archived';
      case 'expired':
        return doc.expirationDate
          ? `expired ${formatDateMedium(doc.expirationDate)}`
          : 'expired';
      case 'pending_future':
        return 'not in effect yet';
      case 'invalid':
        return 'marked invalid';
      default:
        return doc.status;
    }
  }
  // No Verify button exists any more (uploads self-verify), so the actionable remedy for a
  // legacy unverified certificate is to upload it again here rather than to go looking for a
  // control that was removed.
  if (!doc.verifiedAt) return 'not verified — upload it again here to file a verified copy';
  return null;
}

/** `repeating` is the only duration that carries a number worth printing. */
function couponDurationLabel(d: BillingCustomerDiscount): string {
  if (d.duration === 'repeating') {
    return d.durationInMonths ? `repeating ${d.durationInMonths} months` : 'repeating';
  }
  return d.duration;
}

/**
 * WHICH invoices this touches — the one thing the operator has to be able to tell a
 * customer, and the reason this is branched on Stripe's `duration` rather than being a
 * single sentence: `once`, `repeating` and `forever` are three materially different
 * promises. All three exclude hardware.
 */
function couponScopeCopy(
  discount: BillingCustomerDiscount | null,
  previousCouponId?: string | null,
): string {
  if (!discount) {
    return `Future subscription invoices bill at list price${
      previousCouponId ? ` — ${previousCouponId} is no longer on the customer` : ''
    }. Already-finalized invoices are unchanged.`;
  }
  switch (discount.duration) {
    case 'once':
      return 'Applies to the NEXT subscription invoice only — after that the customer is back at list price. Hardware invoices are excluded.';
    case 'repeating':
      return `Applies to every subscription invoice until ${
        discount.endsAt ? formatDayCentral(discount.endsAt) : 'the end of its term'
      }${
        discount.durationInMonths ? ` (${discount.durationInMonths} months from the start)` : ''
      }, then stops on its own. Hardware invoices are excluded.`;
    case 'forever':
      return 'Applies to all future subscription invoices until someone removes it. Hardware invoices are excluded.';
    default:
      return `Stripe reports duration "${discount.duration}" — open the customer below to see which invoices it covers. Hardware invoices are excluded.`;
  }
}

/**
 * The certificate half of Apply Coupon, rendered only for a coupon the catalog flags.
 *
 * Two ways in, one at a time: pick a certificate the account already has, or upload one.
 * The picker is fed from the preview already read for this customer — there is NO second
 * call to the Document Center, so what the operator sees is exactly the list the server's
 * gate will be checked against.
 *
 * Only ELIGIBLE certificates become options. The rest are listed below them, greyed, with
 * the reason. That is deliberate rather than hiding them: "it is on file but nobody has
 * verified it" is the common case, its fix is a step the operator can go and take, and an
 * empty picker for a document they can see in the Document Center would be a dead end.
 */
function TaxExemptDocSection({
  keyId,
  stripeId,
  coupon,
  docs,
  eligibleDocIds,
  documentsAvailable,
  taxStatus,
  value,
  onChange,
  mode,
  onModeChange,
  onUploaded,
  justUploadedId,
}: {
  keyId: string;
  stripeId: string;
  coupon: CouponOption;
  docs: BillingTaxExemptDocument[];
  eligibleDocIds: Set<string>;
  documentsAvailable: boolean;
  /** Stripe's Tax status today: `none` | `exempt` | `reverse`. */
  taxStatus?: string | null;
  value: string;
  onChange: (v: string) => void;
  mode: DocMode;
  onModeChange: (m: DocMode) => void;
  onUploaded: (doc: CustomerDocument) => void;
  justUploadedId: string | null;
}) {
  const options = docs
    .filter((d) => eligibleDocIds.has(d.id))
    .map((d) => ({
      value: d.id,
      // The TYPE leads, because it is the distinction the operator is being asked to make: a
      // certificate filed Tax Exempt may be a reseller's, and only a Non-Profit one says
      // otherwise on its own. Title alone ("2026 exemption.pdf") does not tell you which.
      label: [
        docTypeLabel(d.docType),
        d.title,
        d.jurisdiction,
        d.expirationDate ? `expires ${formatDateMedium(d.expirationDate)}` : 'no expiry',
      ]
        .filter(Boolean)
        .join(' · '),
    }));
  const ineligible = docs.filter((d) => !eligibleDocIds.has(d.id));

  /**
   * What an inline upload FILES the certificate as.
   *
   * Derived from the coupon rather than hardcoded: on NON-PROFIT-10 the document the operator is
   * being asked for IS a non-profit's, so filing it as `tax_exempt` would leave the Document
   * Center unable to tell it from a reseller's — the exact confusion the `non_profit` type was
   * added to remove. A doc-requiring coupon that is NOT about non-profits still files
   * `tax_exempt`, because that is what its evidence is.
   *
   * `grantDocumentTag` is the signal because it is already the server's answer to "is this coupon
   * about non-profits" (`coupon-grant-tags.ts`), re-derived at apply time — so the filing type and
   * the tag cannot disagree.
   */
  const uploadDocType: DocType =
    coupon.grantDocumentTag === 'non-profit' ? 'non_profit' : 'tax_exempt';
  // The canonical lower-kebab slug, matching what the Document Center's own type seeds write and
  // what Apply Coupon stamps — so the apply-time tag write finds it already there instead of
  // adding a second spelling.
  const uploadTag = uploadDocType === 'non_profit' ? 'non-profit' : 'tax-exempt';
  /** The row this operator just uploaded, if it is still the selected one. */
  const uploadedDoc = justUploadedId ? docs.find((d) => d.id === justUploadedId) : undefined;

  return (
    <div className="flex flex-col gap-2.5 rounded-md border border-input px-3 py-2.5">
      <p className="text-xs text-foreground" data-testid={`billing-${keyId}-doc-required`}>
        {`${coupon.label} requires a Non-Profit or tax-exemption certificate. staff-api refuses the discount unless a verified, non-archived Non-Profit or Tax Exempt document exists on this account. Uploading one here files it as a ${DOC_TYPE_LABEL[uploadDocType]} document and verifies it in your name — that records who filed it, and does not mean anyone else reviewed it. A certificate filed before 12 Aug 2026 may show as not usable below; upload it again here to make it usable.`}
      </p>

      {/*
        What picking a certificate DOES to that certificate, said before the operator picks.
        `tax_exempt` covers a reseller's certificate and a non-profit's alike, so this is the
        moment the two get told apart — and the operator should know they are making that
        claim, not discover it afterwards in the Document Center.
      */}
      {coupon.grantDocumentTag ? (
        <p
          className="text-xs text-muted-foreground"
          data-testid={`billing-${keyId}-doc-grant-tag`}
        >
          {`The certificate you pick will be tagged "${coupon.grantDocumentTag}" in the Document Center when the discount is applied — a certificate filed as Tax Exempt may equally belong to a reseller, and the tag is what marks this one as a non-profit's. Nothing is tagged if the apply does not go through.`}
        </p>
      ) : null}

      {/*
        The OTHER thing accepting a certificate does, and the one with a bill attached: it sets
        the customer's Tax status in Stripe. Said before the operator commits, because it reaches
        further than the discount does — Stripe's flag is customer-wide, so it stops tax on every
        invoice in every jurisdiction, including the hardware invoices this console raises with
        US tax, while the certificate itself is for one state.
      */}
      <p className="text-xs text-foreground" data-testid={`billing-${keyId}-tax-advisory`}>
        {taxStatus === 'exempt'
          ? 'Tax status in Stripe is already Exempt — applying will not change it.'
          : taxStatus === 'reverse'
            ? 'Tax status in Stripe is Reverse charge (EU). It is left alone: a US exemption certificate must not overwrite that, so change it by hand in Stripe if it is wrong.'
            : 'Applying will ALSO set this customer\'s Tax status to Exempt in Stripe (Tax information), so future invoices are not taxed. That applies to every invoice in every jurisdiction — including hardware — not only the certificate\'s state. Removing the discount later does not undo it.'}
      </p>

      <RadioGroup
        value={mode}
        onValueChange={(v) => onModeChange(v as DocMode)}
        className="gap-2 sm:grid-cols-2"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem
            value="existing"
            id={`${keyId}-doc-mode-existing`}
            data-testid={`billing-${keyId}-doc-mode-existing`}
          />
          <Label htmlFor={`${keyId}-doc-mode-existing`} className="cursor-pointer text-xs font-normal">
            {`Use a certificate on file${options.length ? ` (${options.length} usable)` : ''}`}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem
            value="upload"
            id={`${keyId}-doc-mode-upload`}
            data-testid={`billing-${keyId}-doc-mode-upload`}
          />
          <Label htmlFor={`${keyId}-doc-mode-upload`} className="cursor-pointer text-xs font-normal">
            {'Upload a new certificate'}
          </Label>
        </div>
      </RadioGroup>

      {mode === 'existing' ? (
        options.length ? (
          <LabeledSelect
            id={`${keyId}-documentId`}
            label="Non-Profit or Tax-exemption certificate"
            value={value}
            onChange={onChange}
            placeholder="Select a verified certificate"
            options={options}
            helper="Only verified, non-archived Non-Profit or Tax Exempt documents on the account staff-api resolved from this customer."
            testId={`billing-${keyId}-documentId`}
          />
        ) : (
          <p
            className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
            data-testid={`billing-${keyId}-doc-empty`}
          >
            {documentsAvailable
              ? `No usable Non-Profit or tax-exemption certificate on this account${
                  docs.length
                    ? ` — ${docs.length} on file, none of them both current and verified (listed below)`
                    : ''
                }. Upload one here, then have it verified in the Document Center.`
              : 'The Document Center is not configured in this environment, so no certificate can be read — which means an empty list here is "cannot look", not "this account has none". A coupon that requires a certificate cannot be applied from this environment.'}
          </p>
        )
      ) : uploadedDoc && value === uploadedDoc.id && eligibleDocIds.has(uploadedDoc.id) ? (
        /*
          Upload finished and the file IS the answer — so say so and stop asking. The earlier
          version flipped back to "use a certificate on file" and left the operator to confirm
          their own upload in a dropdown, which reads as though something went wrong.
          `documentId` is already set, so submit is live at this point.
        */
        <div
          className="flex flex-col gap-1 rounded-md border border-success/40 bg-success/10 px-3 py-2"
          data-testid={`billing-${keyId}-doc-uploaded`}
        >
          <span className="text-xs font-medium text-foreground">
            {`Using “${uploadedDoc.title}”`}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {'Uploaded, filed against this customer and verified in your name — ready to apply.'}
          </span>
          <button
            type="button"
            className="self-start text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={() => onModeChange('existing')}
            data-testid={`billing-${keyId}-doc-use-other`}
          >
            {'Use a different certificate'}
          </button>
        </div>
      ) : (
        <DocumentAttachment
          stripeId={stripeId}
          keyId={keyId}
          docType={uploadDocType}
          tags={[uploadTag]}
          required
          onUploaded={onUploaded}
        />
      )}

      {ineligible.length ? (
        <div className="flex flex-col gap-0.5" data-testid={`billing-${keyId}-doc-unverified`}>
          <span className="text-[11px] text-muted-foreground">
            {ineligible.some((d) => d.id === justUploadedId)
              ? // Only reachable if an upload came back INELIGIBLE, which self-stamping normally
                // prevents — so name the actual reason below rather than assuming it needs
                // verifying, and keep the uploader on screen so they can try another file.
                'Just uploaded, and filed against the customer — but not usable as evidence for the reason below.'
              : 'On file but not usable — these are shown, not offered:'}
          </span>
          <ul className="flex flex-col gap-0.5">
            {ineligible.map((doc) => (
              <li key={doc.id} className="text-[11px] text-muted-foreground">
                {`${docTypeLabel(doc.docType)} · ${doc.title} — ${
                  docIneligibleReason(doc) ?? 'not usable'
                }${doc.id === justUploadedId ? ' (just uploaded)' : ''}`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ApplyCouponForm({
  stripeId,
  customer,
}: {
  /** The console's shared customer — this card no longer owns an id field. */
  stripeId: string;
  customer: StripeCustomerBilling | null;
}) {
  const KEY = 'apply-coupon';
  const empty = { couponKey: '', documentId: '', comment: '' };
  const [f, setF] = useState({ ...empty });
  /**
   * What staff-api last read for this customer — the precondition the apply is gated on.
   * Held locally rather than read off the lazy query so that changing the customer id can
   * DROP it: a preview belonging to a previous customer is worse than none.
   */
  const [preview, setPreview] = useState<BillingCouponPreview | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [docMode, setDocMode] = useState<DocMode>('existing');
  /** Certificates uploaded from this card, folded into a preview read before they existed. */
  const [uploaded, setUploaded] = useState<BillingTaxExemptDocument[]>([]);
  const [justUploadedId, setJustUploadedId] = useState<string | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [result, setResult] = useState<BillingCouponResult | null>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const {
    data: catalogData,
    loading: catalogLoading,
    error: catalogError,
  } = useBillingCouponCatalog();
  const [lookupPreview, { loading: previewLoading }] = useBillingCouponPreview();
  const [applyCoupon] = useBillingApplyCoupon();
  const [removeCoupon] = useBillingRemoveCoupon();

  const catalog = catalogData?.staffBillingCouponCatalog;
  const coupons = catalog?.coupons ?? [];
  const selected = coupons.find((c) => c.couponKey === f.couponKey) ?? null;

  /**
   * Read the customer: the company name that proves the id is the intended tenant, the
   * discount they already have, and the certificates the gate will be checked against.
   *
   * Fired on blur, and unlike the subscription card's address prefill a failure is SHOWN:
   * the apply is gated on this read, so failing quietly would leave the operator with a
   * disabled button and no reason for it.
   */
  const resolvePreview = async () => {
    const id = stripeId.trim();
    if (!CUS_LOOKUP_RE.test(id)) return;
    // A repeat blur on an unchanged id normally costs nothing, so it is skipped. The
    // exception is a certificate waiting on a human verification: re-reading is how the
    // operator picks it up once verified, and this field is the card's only affordance.
    if (preview?.stripeId === stripeId && !justUploadedId) return;
    setLookupError(null);
    try {
      const r = await lookupPreview({ variables: { stripeId } });
      // useLazyQuery resolves with the error rather than rejecting for a GraphQL error,
      // and rejects for some transport failures — so both paths are handled.
      if (r.error || !r.data) {
        setPreview(null);
        setLookupError(r.error?.message ?? 'staff-api returned no customer for that id.');
        return;
      }
      setPreview(r.data.staffBillingCouponPreview);
      setJustUploadedId(null);
    } catch (e) {
      setPreview(null);
      setLookupError((e as Error)?.message ?? 'Could not read the customer.');
    }
  };

  /**
   * Force a re-read after a write, ignoring the unchanged-id short circuit above.
   *
   * Deliberately silent: apply and remove have already happened by the time this runs and
   * the outcome panel is the record of them, so a failed re-read must not be reported as
   * a failed operation.
   */
  const refreshPreview = async () => {
    const id = stripeId.trim();
    if (!CUS_LOOKUP_RE.test(id)) return;
    try {
      const r = await lookupPreview({ variables: { stripeId } });
      if (r.data) setPreview(r.data.staffBillingCouponPreview);
    } catch {
      // See above — the write stands either way.
    }
  };

  /**
   * Changing the customer drops everything read for the previous one: the preview, the
   * certificate list it fed, and the selected document id. The server would refuse a
   * foreign certificate anyway (its gate compares against the account IT resolved, never
   * a client-supplied one), but leaving the id in the field is that same wrong-tenant bug
   * in UI form — one account's certificate offered as justification for another's discount.
   */
  const changeStripeId = (stripeId: string) => {
    setF((s) => ({ ...s, stripeId, documentId: '' }));
    setPreview(null);
    setUploaded([]);
    setJustUploadedId(null);
    setLookupError(null);
    setDocMode('existing');
  };

  /**
   * Select a freshly uploaded certificate — and STAY in upload mode.
   *
   * It deliberately does not flip back to the "certificate on file" picker. Bouncing the
   * operator into a dropdown to re-select the file they just uploaded reads like the upload
   * failed; the section instead confirms what it is now using, with a way out if they want a
   * different one.
   *
   * Since 2026-08-12 an upload self-stamps `verified_at` with the uploader, so the row normally
   * lands ELIGIBLE and submit enables immediately. Eligibility is still read from the returned
   * row rather than assumed — if a future change stops stamping, the row lands ineligible with
   * its reason shown and the uploader stays on screen, instead of enabling submit into a server
   * refusal.
   */
  const onDocUploaded = (doc: CustomerDocument) => {
    const row: BillingTaxExemptDocument = {
      id: doc.id,
      title: doc.title,
      // From the row the upload endpoint returned, not from what the dialog was asked to file:
      // the option label must say what the document actually IS.
      docType: doc.docType,
      status: doc.status,
      expirationDate: doc.expirationDate,
      verifiedAt: doc.verifiedAt,
      jurisdiction: doc.jurisdiction,
    };
    setUploaded((s) => [...s.filter((d) => d.id !== row.id), row]);
    setF((s) => ({ ...s, documentId: row.id }));
    setJustUploadedId(row.id);
  };

  const docs = useMemo(() => {
    const listed = preview?.taxExemptDocuments ?? [];
    const extra = uploaded.filter((u) => !listed.some((d) => d.id === u.id));
    return [...listed, ...extra];
  }, [preview, uploaded]);

  /** MIRRORS the server gate: status ∈ {valid, expiring_soon} AND `verifiedAt` non-null. */
  const eligibleDocIds = useMemo(
    () => new Set(docs.filter((d) => docIneligibleReason(d) === null).map((d) => d.id)),
    [docs],
  );

  const comment = f.comment.trim();
  const overLimit = comment.length > INVOICE_COMMENT_MAX;
  const id = stripeId.trim();
  const discount = preview?.currentDiscount ?? null;
  // Same fallback chain the customer bar uses, so the preview never prints a bare accountId
  // when a human-readable handle exists.
  const companyLabel = preview
    ? (preview.companyName ?? customer?.ownerEmail ?? preview.accountId)
    : stripeId;
  /** Both names are read from different systems, so a mismatch is worth surfacing. */
  const nameMismatch =
    !!preview?.companyName &&
    !!preview?.stripeName &&
    preview.companyName.trim().toLowerCase() !== preview.stripeName.trim().toLowerCase();

  /**
   * Every clause is a precondition the operator can SEE on the card. The document clause
   * is the advisory mirror of the server's gate; the `!discount` clause is not a nicety —
   * Stripe keeps no history of a discount this would displace.
   */
  const valid =
    /^cus_/i.test(stripeId) &&
    !!preview &&
    preview.stripeId === stripeId &&
    !previewLoading &&
    !discount &&
    !!selected &&
    (!selected.requiresTaxExemptDoc || eligibleDocIds.has(f.documentId)) &&
    comment.length > 0 &&
    !overLimit;

  /** A removal is a price increase, so its reason is the whole value of the audit row. */
  const removeValid = comment.length > 0 && !overLimit;

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    setResult(null);
    try {
      const r = (
        await applyCoupon({
          variables: {
            input: {
              stripeId,
              // The catalog KEY, never `selected.stripeCouponId`: staff-api re-resolves
              // the coupon and its tax-exempt flag from its own row, so nothing sent from
              // here can attach a coupon nobody approved or talk the gate out of asking.
              couponKey: f.couponKey,
              // Omitted for an unflagged coupon: the server validates a certificate it is
              // given regardless of the flag, so sending a leftover id would be a refusal
              // for a coupon that never needed one.
              ...(selected?.requiresTaxExemptDoc ? { taxExemptDocumentId: f.documentId } : {}),
              comment,
            },
          },
        })
      ).data!.staffBillingApplyCoupon;
      setResult(r);
      // The customer HAS a discount now, so the preview this form is gated on is stale:
      // re-read it, which is what collapses the form into the existing-discount banner and
      // stops a second apply from being offered.
      await refreshPreview();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  const doRemove = async () => {
    setRemoveOpen(false);
    setBusy(true);
    setMsg(null);
    setResult(null);
    try {
      const r = (await removeCoupon({ variables: { stripeId, comment } })).data!
        .staffBillingRemoveCoupon;
      setResult(r);
      await refreshPreview();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * The preview used to fire on blur of this card's own id field. That field is gone — the
   * console owns one — so the read is driven by the shared id changing instead, on the same
   * debounce the console uses.
   *
   * The reset half matters as much as the read: when the customer changes, the previous
   * customer's preview, discount and chosen certificate must all go. This card's whole job is
   * attaching a discount to a named customer, so a stale preview beside a new id is the one
   * state it must never show.
   */
  useEffect(() => {
    const next = stripeId.trim();
    setPreview(null);
    setF((prev) => (prev.documentId ? { ...prev, documentId: '' } : prev));
    if (!CUS_LOOKUP_RE.test(next)) return;
    const timer = setTimeout(() => {
      void resolvePreview();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeId]);

  const clear = () => {
    setF({ ...empty });
    setPreview(null);
    setUploaded([]);
    setJustUploadedId(null);
    setDocMode('existing');
    setLookupError(null);
    setMsg(null);
    setResult(null);
  };

  /**
   * Collapsed until a customer id is entered. The comment records WHO approved a discount on
   * WHICH customer, so asking for it before the customer is named puts the reason before the
   * subject.
   */
  const ready = /^cus_\w{6,}$/i.test(stripeId.trim());

  return (
    <CardShell
      testId={`billing-card-${KEY}`}
      title="Apply Stripe Coupon"
      description="Attaches a catalog-approved Stripe coupon to the CUSTOMER, so it discounts every future subscription invoice for the coupon's duration — nothing is charged or refunded now. Stripe holds exactly ONE customer-level discount and keeps no history of one it displaced, so an existing discount has to be removed first. Hardware invoices are never discounted. If a discount does not show up on an invoice, check the subscription in Stripe — a subscription-level coupon overrides a customer one."
    >
      {catalogError || catalog?.couponsUnavailable ? (
        <p
          className={
            catalogError
              ? 'rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive'
              : 'rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning'
          }
          data-testid={`billing-${KEY}-catalog-error`}
        >
          {catalogError
            ? `Could not load the coupon catalog: ${catalogError.message}`
            : 'The coupon catalog is not migrated in this environment, so there is nothing to apply. This is a missing table, not an empty list — it needs migration 011 run against this database.'}
        </p>
      ) : null}

      {/*
        The coupon picker is gated too, not just the comment. It shared a grid with the comment
        and so survived the earlier gate by accident — but picking a coupon before naming a
        customer is choosing a discount for nobody, and the preview underneath cannot say what
        it would do without both.
      */}
      {ready ? (
        <>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <LabeledSelect
            id={`${KEY}-couponId`}
            label="Coupon"
            // The catalog KEY is the value: no Stripe coupon id ever leaves this card.
            value={f.couponKey}
            onChange={set('couponKey')}
            placeholder={
              catalogLoading ? 'Loading…' : coupons.length ? 'Select coupon' : 'No coupons configured'
            }
            options={coupons.map((c) => ({ label: c.label, value: c.couponKey }))}
            helper="Active rows from the staff-owned coupon catalog — ROOT maintains it on Administration → Products List."
            testId={`billing-${KEY}-couponId`}
          />
          {/*
            Comment sits beside the coupon rather than below the whole card. They are the two
            things this action needs from the operator — which coupon, and who approved it — so
            reading them together reads as one decision.
          */}
            <LabeledInput
            id={`${KEY}-comment`}
            label="Comment (recorded in the audit trail)"
            value={f.comment}
            onChange={set('comment')}
            placeholder="Tax-exempt certificate has been verified"
            helper={`Required — who approved this discount and why. Not customer-visible: it is not written to Stripe. Also required to REMOVE a discount. ${comment.length}/${INVOICE_COMMENT_MAX}`}
            testId={`billing-${KEY}-comment`}
            />
        </div>
          {overLimit ? (
            <p
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              data-testid={`billing-${KEY}-overlimit`}
            >
              {`Comment is ${comment.length} characters — at most ${INVOICE_COMMENT_MAX} are accepted.`}
            </p>
          ) : null}
        </>
      ) : null}

      {selected ? (
        // Renders ONLY when it has something the picker does not already say.
        //
        // Dropped: the Stripe coupon id (internal, and this card's whole point is that a
        // Stripe id never leaves the catalog — showing it here contradicted that), the coupon
        // KEY (the picker label above already names it), and the "no terms recorded" filler.
        // Kept: real terms text, and the tax-exemption requirement — that one gates the apply,
        // so hiding it would leave an operator with a disabled button and no reason for it.
        selected.terms || selected.requiresTaxExemptDoc ? (
          <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-terms`}>
            {[
              selected.terms,
              selected.requiresTaxExemptDoc
                ? 'needs a verified Non-Profit or tax-exemption certificate'
                : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        ) : null
      ) : null}

      {previewLoading ? (
        <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-customer-loading`}>
          {'Reading the customer…'}
        </p>
      ) : null}
      {lookupError ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid={`billing-${KEY}-lookup-error`}
        >
          {`Could not read that customer: ${lookupError}`}
        </p>
      ) : null}
      {/*
        The identity restatement is gone: the console's customer bar names the customer once,
        directly above the only card on screen, so repeating "acct — cus_…" here was noise.
        
        What survives is the MISMATCH, promoted to its own warning. It is not identification,
        it is a safety check — the two names come from different systems (the company doc and
        Stripe), and disagreeing ones mean you may be about to discount the wrong company.
      */}
      {/*
        Same element, different job. It used to restate "acct — cus_…", which the console's
        customer bar now says once directly above. What it reports instead is the fact this
        card actually needs before you apply anything: whether the customer ALREADY has a
        discount — Stripe holds exactly one and keeps no history of one it displaced, so an
        existing discount has to be removed first. It doubles as the signal that this card's
        own preview read has landed.
      */}
      {preview && !previewLoading ? (
        <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-customer-summary`}>
          {discount
            ? 'This customer already has a customer-level discount — it must be removed before another is applied.'
            : 'No customer-level discount on this customer today.'}
        </p>
      ) : null}

      {preview && !previewLoading && nameMismatch ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${KEY}-name-mismatch`}
        >
          {`⚠ Stripe calls this customer "${preview.stripeName}" — confirm it is the same company before discounting it.`}
        </p>
      ) : null}

      {discount ? (
        <div
          className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${KEY}-existing-discount`}
        >
          <span>
            {`${companyLabel} already has ${discount.couponName ?? discount.couponId} (${
              discount.summary
            }, ${couponDurationLabel(discount)}) since ${formatDayCentral(
              discount.startedAt,
            )}. Applying another would displace it and Stripe keeps no record of what it displaced — so remove this one first, using the Comment box ABOVE as the reason.`}
          </span>
          <div>
            <Button
              variant="destructive"
              size="sm"
              disabled={!removeValid || busy}
              onClick={() => setRemoveOpen(true)}
              data-testid={`billing-${KEY}-remove-btn`}
            >
              {busy ? 'Working...' : 'Remove discount'}
            </Button>
            {!removeValid ? (
              <span className="ml-2 text-[11px]">
                {'Add a comment in the Comment box above (beside Coupon) — it is the only record of why the discount went, and Remove stays disabled until it has one.'}
              </span>
            ) : null}
          </div>

          <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
            <AlertDialogContent data-testid={`billing-${KEY}-remove-confirm`}>
              <AlertDialogHeader>
                <AlertDialogTitle>{'Remove this discount?'}</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="flex flex-col gap-2 text-xs">
                    <span>
                      <span className="font-medium text-foreground">{companyLabel}</span>
                      {` loses ${discount.couponName ?? discount.couponId} (${
                        discount.summary
                      }) from their next subscription invoice onwards. For them this is a price increase.`}
                    </span>
                    <span>
                      {'Already-finalized invoices are unchanged. Stripe keeps no history of a removed discount, so the comment you entered is the only record of why it went.'}
                    </span>
                    {/*
                      The second money change, named before it happens. It applies only when the
                      coupon being removed is one that REQUIRED a certificate — that coupon is
                      why the customer was exempt, so taking it away ends the exemption. The
                      server re-checks that against the catalog; this copy only warns.
                    */}
                    {preview?.taxStatus === 'exempt' ? (
                      <span data-testid={`billing-${KEY}-remove-tax-warning`}>
                        {'Tax status: if this coupon is one that required a certificate, removing it also sets the customer back to Taxable, so their invoices are taxed again. A coupon that never required one leaves the exemption alone.'}
                      </span>
                    ) : null}
                    <span className="text-foreground">{`Comment: ${comment}`}</span>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid={`billing-${KEY}-remove-cancel`}>
                  {'Cancel'}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void doRemove();
                  }}
                  data-testid={`billing-${KEY}-remove-go`}
                >
                  {'Remove discount'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : null}

      {selected?.requiresTaxExemptDoc ? (
        <TaxExemptDocSection
          keyId={KEY}
          stripeId={stripeId}
          coupon={selected}
          docs={docs}
          eligibleDocIds={eligibleDocIds}
          documentsAvailable={preview?.documentsAvailable ?? false}
          taxStatus={preview?.taxStatus}
          value={f.documentId}
          onChange={set('documentId')}
          mode={docMode}
          onModeChange={setDocMode}
          onUploaded={onDocUploaded}
          justUploadedId={justUploadedId}
        />
      ) : null}


      {/*
        The preview stays OUTSIDE the gate. Its no-customer branch already says "Enter the
        customer and pick a coupon", which is exactly the guidance a collapsed card needs — so
        it doubles as the awaiting-customer hint rather than being hidden behind one.
      */}
      <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-preview`}>
        {selected && preview
          ? `Attaches ${selected.label} to ${companyLabel} as their one customer-level discount. It lands on future subscription invoices only — nothing is charged or refunded now, already-finalized invoices are untouched, and hardware invoices are excluded.`
          : 'Enter the customer and pick a coupon — what it would do appears here before anything is applied.'}
      </p>
      <ActionButtons
        keyId={KEY}
        submitLabel="Apply Coupon"
        valid={valid}
        busy={busy}
        onSubmit={submit}
        onClear={clear}
      />
      <ResultLine msg={msg} testId={`billing-${KEY}-result`} />
      {result ? <ApplyCouponOutcome result={result} /> : null}
    </CardShell>
  );
}

/**
 * The discount as Stripe holds it AFTER the write — one panel for both apply and remove,
 * because the mutations return the same shape and read the same either way: `discount` set
 * means it is on, `previousCouponId` set means it came off.
 *
 * The Stripe link is the verification step, not decoration: `customers.update` has no
 * compare-and-set, so the customer in the dashboard is the only authority on what a
 * concurrent operator may have done to it.
 */
function ApplyCouponOutcome({ result }: { result: BillingCouponResult }) {
  const KEY = 'apply-coupon';
  const discount = result.discount ?? null;
  return (
    <div className="flex flex-col gap-2" data-testid={`billing-${KEY}-outcome`}>
      <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-summary`}>
        {[
          result.companyName ?? result.accountId,
          discount
            ? (discount.couponName ?? discount.couponId)
            : `removed ${result.previousCouponId ?? 'the discount'}`,
          discount?.summary,
          discount ? couponDurationLabel(discount) : null,
        ]
          .filter(Boolean)
          .join(' — ')}
      </p>
      <p className="text-xs text-muted-foreground" data-testid={`billing-${KEY}-scope`}>
        {couponScopeCopy(discount, result.previousCouponId)}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <a
          href={result.customerUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium text-primary underline underline-offset-2"
          data-testid={`billing-${KEY}-customer-link`}
        >
          {'Open the customer in Stripe →'}
        </a>
      </div>
      {result.taxExemptDocumentId ? (
        <p className="text-[11px] text-muted-foreground" data-testid={`billing-${KEY}-doc-link`}>
          {`Justified by ${result.taxExemptDocumentTitle ?? 'certificate'} (${
            result.taxExemptDocumentId
          })`}
          {result.documentLinked
            ? ' — recorded against that certificate in the Document Center.'
            : ' — but the note against that certificate could NOT be written. The discount is applied; add the cross-reference by hand rather than re-applying, which would be a second apply.'}
          {/* The tag is the visible half of the back-reference, so it gets its own sentence.
              A tag that was DUE and did not land is reported in `message`, not here — this
              line only ever states what IS true of the certificate now. */}
          {result.documentTagApplied
            ? ` Tagged "${result.documentTagApplied}".`
            : ''}
        </p>
      ) : null}
      {/* Tax status gets its own line whenever there is one to report: it is a second money
          change, and "the discount applied but the tax flag did not" has to be legible. */}
      {result.taxStatus ? (
        <p className="text-[11px] text-muted-foreground" data-testid={`billing-${KEY}-tax-status`}>
          {result.taxStatusChanged
            ? result.previousCouponId
              ? 'Tax status set back to Taxable in Stripe — invoices are taxed again.'
              : 'Tax status set to Exempt in Stripe — future invoices are not taxed, in every jurisdiction.'
            : result.taxStatus === 'exempt'
              ? 'Tax status in Stripe: Exempt (unchanged by this request).'
              : result.taxStatus === 'reverse'
                ? 'Tax status in Stripe: Reverse charge (EU) — deliberately left as it was.'
                : result.previousCouponId
                  ? `Tax status in Stripe: ${result.taxStatus}.`
                  : `Tax status in Stripe: ${result.taxStatus}. If this customer should be exempt, set it under Tax information in Stripe — re-applying would be a second apply.`}
        </p>
      ) : null}
      <p className="text-[11px] text-muted-foreground">{result.message}</p>
    </div>
  );
}

/**
 * The console's ONE customer field, and the identity it resolves to.
 *
 * Seven of the cards below act on the same customer, and before this they each carried their
 * own `cus_…` box — so an operator typed it up to seven times, and, worse, two cards could
 * hold two DIFFERENT customers at once with nothing on screen saying so. The id lives here
 * now and the cards read it.
 *
 * The ID is what gates the cards. The RESOLVED identity beside it is decoration and must stay
 * that way: `staffBillingStripeCustomer` needs `Customers: Read` and answers 503 when the
 * Stripe key is unset, so making the console depend on it would turn one flaky read into a
 * dead page. Every card re-validates the id server-side regardless.
 *
 * Deliberately NOT the subscription snapshot, which is richer: that query is
 * subscription-specific and refuses a card-on-file customer, so it would report "No activated
 * Subscription" for a customer who is merely being onboarded — true for one card, wrong as a
 * statement about the customer. Cards needing plan / screens / add-ons read that themselves.
 */
function CustomerBar({
  stripeId,
  onChange,
  onBlur,
  customer,
  loading,
}: {
  stripeId: string;
  onChange: (v: string) => void;
  /** Immediate resolve, so tabbing out does not wait out the debounce. */
  onBlur: () => void;
  customer: StripeCustomerBilling | null;
  loading: boolean;
}) {
  const valid = /^cus_\w{6,}$/i.test(stripeId.trim());
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border bg-card p-4 shadow-sm"
      data-testid="billing-customer-bar"
    >
      {/*
        Half width, via the same two-column grid every field in this console sits in — so the
        one input above the tab row lines up with the fields below it instead of stretching the
        full card. A `cus_…` is ~22 characters; the rest was empty box.
      */}
      <div className="grid gap-3 sm:grid-cols-2">
      <div onBlur={onBlur}>
      <LabeledInput
        id="billing-customer"
        label="Customer Stripe ID"
        value={stripeId}
        onChange={onChange}
        placeholder="cus_..."
        helper="Enter the customer Stripe ID (e.g., cus_ABC123) — every card below acts on this customer."
        testId="billing-customer-stripeId"
      />
      </div>
      </div>
      {loading ? (
        <p className="text-xs text-muted-foreground" data-testid="billing-customer-loading">
          {'Looking up the customer…'}
        </p>
      ) : customer ? (
        <p className="text-xs text-muted-foreground" data-testid="billing-customer-identity">
          {/*
            Identify the customer by something a human recognises. The accountId is a LAST
            resort, not the default: it names the tenant but tells the operator nothing, and
            plenty of accounts carry no company name at all — which is when the owner's email
            is the only useful handle.
          */}
          {[
            customer.companyName,
            // Labelled: a bare email reads as stray text next to a company name, and an
            // operator scanning this line should not have to infer what each value IS.
            customer.ownerEmail ? `Owner email: ${customer.ownerEmail}` : null,
            // Same reasoning, and this one only appears when there is neither a name nor an
            // owner to show — so it is the case that most needs saying what it is.
            !customer.companyName && !customer.ownerEmail
              ? `Account: ${customer.accountId}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          {customer.hasAddress ? '' : ' · ⚠ no billing address in Stripe'}
        </p>
      ) : valid ? (
        // The lookup failed or is unavailable. Say so, but do NOT block: the cards open on the
        // id alone and each one checks it server-side.
        <p className="text-xs text-muted-foreground" data-testid="billing-customer-unresolved">
          {'Could not read this customer from Stripe — the cards below still work, and each one checks the id when you submit.'}
        </p>
      ) : // Nothing when the field is empty: the helper directly above already says to enter a
      // customer Stripe ID, and each card states its own gate. A third line saying the same
      // thing was the emptiest state on the page carrying the most text.
      null}
    </div>
  );
}

export function BillingConsole({
  initialAction = 'coupon',
  initialLegacy = 'normal',
  dunningOnly = false,
}: {
  /**
   * Which action opens first.
   *
   * Defaulted, so the console's own callers pass nothing. It exists because the tab row owns
   * which form is mounted, and that needs to be settable from outside — by a spec that wants
   * to exercise one card, and (next) by a `?action=` query param so a card can be linked to.
   */
  initialAction?: string;
  initialLegacy?: string;
  /**
   * BILLING-role view (requester, 2026-09-23): the customer bar and the Dunning control, and
   * NOTHING else — no action tabs, no Quote Generator. The API is the control (billing is
   * admitted only to the customer lookup and the dunning write); this keeps the page from
   * offering tools that would refuse them.
   */
  dunningOnly?: boolean;
} = {}) {
  const [updateCompanyScreens] = useBillingUpdateCompanyScreens();
  const [updateTeamScreens] = useBillingUpdateTeamScreens();

  /**
   * The shared customer. Owned here rather than in a context: every consumer is a direct
   * child rendered a few lines below, so a provider would add indirection without removing
   * any plumbing, and props keep each card independently testable.
   */
  /** Which action is showing. One form at a time — see the tab row below. */
  const [action, setAction] = useState(initialAction);
  const [legacy, setLegacy] = useState(initialLegacy);

  const [stripeId, setStripeId] = useState('');
  const [customer, setCustomer] = useState<StripeCustomerBilling | null>(null);
  const [lookupCustomer, { loading: customerLoading }] = useBillingStripeCustomer();
  const loadedIdRef = useRef<string | null>(null);

  /**
   * Resolve on a debounce as the id is typed. The first half is the safety-critical part:
   * the moment the id changes, the resolved identity is discarded, so the strip can never
   * describe one customer while the field names another — which is the whole failure this
   * change exists to remove, and would be worse here than per-card since seven cards read it.
   */
  useEffect(() => {
    const id = stripeId.trim();
    if (loadedIdRef.current && loadedIdRef.current !== id) {
      loadedIdRef.current = null;
      setCustomer(null);
    }
    if (!/^cus_\w{6,}$/i.test(id) || loadedIdRef.current === id) return;
    const timer = setTimeout(() => void resolveCustomer(id), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeId]);

  /** One read, callable from the debounce above and from the bar's blur. */
  const resolveCustomer = async (raw?: string) => {
    const id = (raw ?? stripeId).trim();
    if (!/^cus_\w{6,}$/i.test(id) || loadedIdRef.current === id) return;
    loadedIdRef.current = id;
    try {
      const r = await lookupCustomer({ variables: { stripeId: id } });
      setCustomer(r.data?.staffBillingStripeCustomer ?? null);
    } catch {
      // Release the id so editing retries; the cards do not depend on this read.
      loadedIdRef.current = null;
      setCustomer(null);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="billing-console">
      {/*
        Order is deliberate: the tools that MOVE MONEY in Stripe come first, and the
        Mongo-only ones sit at the bottom under their "Only Mongo Updated - " prefix. An
        operator reaching for this console is almost always doing Stripe work, and the legacy
        Mongo-only cards are the ones they should have to scroll to rather than land on.
      */}
      {dunningOnly ? null : <QuoteGeneratorCard />}
      {dunningOnly ? null : <StripeQuoteGeneratorCard />}
      <CustomerBar
        stripeId={stripeId}
        onChange={setStripeId}
        onBlur={() => void resolveCustomer()}
        customer={customer}
        loading={customerLoading}
      />

      {/*
        ONE action at a time, chosen from a tab row, rather than ten stacked cards.
        
        This is what hoisting the customer made possible: the old objection to showing one
        card at a time was that switching meant re-typing the customer, and the customer now
        lives above the row. Nine card shells, nine repeated "Acting on cus_…" lines and nine
        descriptions come off the page with it.
        
        PILL style deliberately, not the underlined SECTION_TAB_* one OptiHubPage uses for its
        own row — `lib/section-tabs.ts` says why in as many words: "two pill rows stacked read
        as one confused control rather than a hierarchy", and the same is true of two underlined
        rows. The outer row is underlined so this inner one can be pills.
        
        Radix UNMOUNTS the inactive form, which is what actually puts one form on screen.
        `forceMount` was tried first and rejected on evidence: it leaves every panel rendered
        and visible in this build, so the page would have been exactly as long as before. A
        test asserts one visible card for that reason.
        
        The cost is real and accepted: switching tabs discards a half-filled form. The
        mitigation is that each action is short and the customer — the one field worth not
        retyping — lives above the row and survives every switch.
      */}
      {dunningOnly ? (
        <div className="flex justify-end" data-testid="billing-dunning-only">
          <DunningExclusionControl
            customer={customer}
            onChanged={(value, untilDate) =>
              setCustomer((c) =>
                c ? { ...c, dunningExclusion: value, dunningExclusionUntil: untilDate } : c,
              )
            }
          />
        </div>
      ) : (
      <Tabs value={action} onValueChange={setAction}>
        {/*
          The Dunning control sits at the RIGHT END of the row, OUTSIDE the TabsList: it is a
          per-customer flag rather than an action form, so it must be visible whichever tab is
          open — and keeping it out of the list keeps the tab ids exactly what they were.
        */}
        <div className="flex flex-wrap items-center justify-between gap-2">
        <TabsList data-testid="billing-action-tabs">
          <TabsTrigger value="coupon" data-testid="billing-tab-coupon">
            {'Coupon'}
          </TabsTrigger>
          <TabsTrigger value="onboard" data-testid="billing-tab-onboard">
            {'Onboard'}
          </TabsTrigger>
          <TabsTrigger value="screens" data-testid="billing-tab-screens">
            {'+Screens'}
          </TabsTrigger>
          <TabsTrigger value="hardware" data-testid="billing-tab-hardware">
            {'Hardware'}
          </TabsTrigger>
          <TabsTrigger value="invoice" data-testid="billing-tab-invoice">
            {'Invoice'}
          </TabsTrigger>
          <TabsTrigger value="legacy" data-testid="billing-tab-legacy">
            {'Legacy'}
          </TabsTrigger>
        </TabsList>
        <DunningExclusionControl
          customer={customer}
          onChanged={(value, untilDate) =>
            setCustomer((c) =>
              c ? { ...c, dunningExclusion: value, dunningExclusionUntil: untilDate } : c,
            )
          }
        />
        </div>

        <TabsContent value="coupon">
          <ApplyCouponForm stripeId={stripeId} customer={customer} />
        </TabsContent>
        <TabsContent value="onboard">
          <CreateStripeSubscriptionForm stripeId={stripeId} customer={customer} />
        </TabsContent>
        <TabsContent value="screens">
          <ChangeSubscriptionForm stripeId={stripeId} customer={customer} />
        </TabsContent>
        <TabsContent value="hardware">
          <CreateHardwareInvoiceForm stripeId={stripeId} customer={customer} />
        </TabsContent>
        <TabsContent value="invoice">
          {/*
            The one action NOT scoped to the customer above: it keys on an invoice reference,
            so it says so rather than letting an operator assume the bar applies.
          */}
          <p className="mb-2 text-xs text-muted-foreground" data-testid="billing-invoice-scope-note">
            {'This action keys on an INVOICE, not the customer above.'}
          </p>
          <SetInvoiceStatusForm />
        </TabsContent>

        <TabsContent value="legacy">
          {/*
            The Mongo-only tools, behind one tab because they are the ones nobody reaches for
            first. The heading carries the warning the four titles used to repeat.
          */}
          <div className="flex flex-col gap-3">
            <p className="text-xs text-muted-foreground" data-testid="billing-legacy-note">
              {'These write Mongo only and never touch Stripe. Teams Billing keys on a TEAM\'s Stripe id, which is a different object from the customer above.'}
            </p>
            <Tabs value={legacy} onValueChange={setLegacy}>
              <TabsList data-testid="billing-legacy-tabs">
                <TabsTrigger value="normal" data-testid="billing-tab-legacy-normal">
                  {'Normal Onboarding'}
                </TabsTrigger>
                <TabsTrigger value="userful" data-testid="billing-tab-legacy-userful">
                  {'Userful Onboarding'}
                </TabsTrigger>
                <TabsTrigger value="screens" data-testid="billing-tab-legacy-screens">
                  {'Screen Quantity'}
                </TabsTrigger>
                <TabsTrigger value="teams" data-testid="billing-tab-legacy-teams">
                  {'Teams Billing'}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="normal">
                <NormalOnboardingForm stripeId={stripeId} customer={customer} />
              </TabsContent>
              <TabsContent value="userful">
                <UserfulOnboardingForm stripeId={stripeId} customer={customer} />
              </TabsContent>
              <TabsContent value="screens">
                <ScreenQtyForm
                  sharedStripeId={stripeId}
                  customer={customer}
                  keyId="update-company-screens"
                  title="Only Mongo Updated - Update Screen Quantity"
                  description="Increase or decrease the number of screens for a customer subscription."
                  onSubmit={async (v) => {
                    const r = (
                      await updateCompanyScreens({
                        variables: {
                          stripeId: v.stripeId,
                          totalQuantity: Number(v.total),
                          previousQuantity: Number(v.previous),
                        },
                      })
                    ).data!.staffBillingUpdateCompanyScreens;
                    return r.companyName ? `${r.companyName} — ${r.message}` : r.message;
                  }}
                />
              </TabsContent>
              <TabsContent value="teams">
                <ScreenQtyForm
                  // The console customer is passed for the GATE only; `ownId` keeps this
                  // instance acting on its own team id, a different Stripe object.
                  sharedStripeId={stripeId}
                  ownId
                  keyId="update-team-screens"
                  title="Only Mongo Updated - Teams Billing – Adjusting the Number of Screens"
                  description="Increase or decrease the number of screens for a team subscription."
                  onSubmit={async (v) => {
                    const r = (
                      await updateTeamScreens({
                        variables: {
                          stripeId: v.stripeId,
                          totalQuantity: Number(v.total),
                          previousQuantity: Number(v.previous),
                        },
                      })
                    ).data!.staffBillingUpdateTeamScreens;
                    return r.companyName ? `${r.companyName} — ${r.message}` : r.message;
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}
